/**
 * aggregates (frota) — a aritmética e o VOCABULÁRIO das seis telas de frota,
 * em módulo PURO.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTE ARQUIVO EXISTE
 *
 * A tela Subestação acumulou DEZOITO defeitos de uma única família — "o
 * agregado trata a ausência ou a IDADE da leitura como se fosse dado válido" —
 * e só parou de reincidir quando o critério passou a existir UMA vez. As seis
 * telas de frota nunca foram auditadas para essa família.
 *
 * E a frota tem um EIXO QUE A SUBESTAÇÃO NÃO TEM.
 *
 * Em 29/09/2026 o portal trocou de coletor. Não passou a ver a mesma frota com
 * menos dados: passou a ver OUTRA FROTA (medido `em_ambos = 0` — 70 veículos
 * `000xx` do FleetBus contra 16 `82xxx` do Portal VGPL), e a fonte nova é
 * INCAPAZ de entregar 18 campos que estas telas renderizam. `range_km` é 0 de
 * 16 — não porque os ônibus não têm autonomia, mas porque a fonte não publica
 * autonomia. Nunca vai publicar.
 *
 * Isso é IMPOSSIBILIDADE, e é diferente das três coisas que a Subestação já
 * sabia dizer:
 *
 *   AUSÊNCIA        `soc_pct` em 2 de 16 — a fonte fornece e estes dois não
 *                   mandaram. Esperar PODE resolver.
 *   VENCIMENTO      10 de 16 parados na origem, dois desde 1º/set — o valor
 *                   existe e é velho. Degrada, não desaparece.
 *   CARIMBO FUTURO  `age_seconds` negativo. O `datalbus_sync.sql` faz
 *                   `AT TIME ZONE 'UTC'` à mão; erro de fuso carimba 16
 *                   veículos 3 h no futuro de uma vez.
 *   IMPOSSIBILIDADE `range_km` em 0 de 16 — esperar NÃO resolve, e dizer
 *                   "ainda não" é promessa falsa.
 *
 * ---------------------------------------------------------------------------
 * DOIS EIXOS, ORTOGONAIS
 *
 * Eixo 1 — ESTADO DA LEITURA, por veículo: `EstadoDeLeitura` de
 *          `@nxvolt/shared/frescor`. Já existe, não é redefinido aqui.
 * Eixo 2 — SUPORTE DO CAMPO PELA FONTE: propriedade do par (fonte, campo), e
 *          por isso igual para os 16 veículos ao mesmo tempo.
 *
 *  |                        | fonte fornece                 | fonte NÃO fornece |
 *  |------------------------|-------------------------------|-------------------|
 *  | `latest === null`      | "sem leitura para este ônibus" | não fornecido    |
 *  | `age > limiar`         | valor + "(último)" + degrada   | não fornecido    |
 *  | `age < -5`             | "carimbo à frente do relógio"  | não fornecido    |
 *  | corrente, campo `null` | "leitura sem <grandeza>"       | não fornecido    |
 *  | corrente, campo tem    | o valor                        | **IMPOSSÍVEL**   |
 *
 * A coluna da direita COLAPSA — é essa economia que justifica o eixo separado:
 * 16 veículos × 4 estados não precisam de 64 frases. E a última célula é o
 * ponto: campo declarado impossível chegando COM valor é inconsistência de
 * fonte, e tem de aparecer na tela em vez de ser engolida.
 *
 * ---------------------------------------------------------------------------
 * O CLASSIFICADOR É UM: `estadoDoCampo`
 *
 * Nenhuma das seis telas reaplica regra: elas recebem estado classificado e
 * frase pronta, e só decidem apresentação (cor, opacidade, ícone). Foi a
 * ausência disso que produziu, na Subestação, um nó dizendo "1 de 3 sem leitura
 * CORRENTE" sobre um trafo que o nó ao lado marcava "sem leitura" — o
 * vocabulário escorregava só no caminho dos ignorados.
 *
 * ---------------------------------------------------------------------------
 * DUAS DIVERGÊNCIAS DELIBERADAS EM RELAÇÃO A `frescor.ts`, REGISTRADAS PORQUE
 * QUEM AUDITAR VAI ESTRANHAR:
 *
 * 1. `baseDeAgregados` de `frescor.ts` NÃO é usado, e nenhuma
 *    `BaseDeAgregados<T>` é produzida aqui. A política de lá é
 *    "correntes > 0 ? frescos : todos", certa para a Subestação, ERRADA para a
 *    frota: com 10 de 16 veículos parados na origem, ela expulsaria os 10 do
 *    SOC médio sempre que um único veículo estivesse fresco. O ADR é explícito
 *    — o veículo parado fica VISÍVEL e DEGRADADO, e trocar "sem sinal" por
 *    "sem frota" é a mentira pior.
 *
 *    Em vez da marca no tipo, os agregados RECEBEM A TOPOLOGIA e aplicam o
 *    critério por dentro (`mediaDoCampo(veiculos, campo, cfg)`).
 *
 *    A COMPENSAÇÃO QUE EU ALEGUEI ESTAVA ERRADA, e a revisão provou. Eu
 *    escrevi que "a propriedade fica mais forte: não existe lista a passar".
 *    Não é verdade. A marca protegia contra passar a lista CRUA onde se queria
 *    a filtrada; o risco da frota é o INVERSO — passar a FILTRADA onde se quer
 *    a topologia. O revisor injetou na tela:
 *
 *        mediaDoCampo(veiculos.filter((v) => v.latest !== null), 'soc_pct', cfg)
 *
 *    `tsc` limpo, suíte inteira verde, e o denominador vira "quem respondeu" —
 *    o quarto invariante do ADR violado sem que nada visse.
 *
 *    O QUE FECHA: `TopologiaDeFrota`, uma marca nominal sobre o array que só
 *    `topologiaDeFrota()` produz. O primeiro argumento dos agregados deixou de
 *    ser `VeiculoDeFrota[]` — e o resultado de um `.filter()` não é atribuível
 *    a ele. É a mesma técnica de `BaseDeAgregados`, apontada para o outro lado.
 *
 * 2. `discriminarPorCausa` de `frescor.ts` NÃO é usado; há um
 *    `discriminarPorCausaDeCampo` local. O de lá classifica o excluído por
 *    `estadoDeLeitura`, o que é certo sob a política dele (vencido é excluído
 *    POR estar vencido). Sob a política daqui o vencido CONTRIBUI, então um
 *    veículo vencido que ficou fora só pode ter ficado por falta da grandeza —
 *    e o de lá o jogaria no balde `vencidos`, fazendo o qualificador dizer
 *    "com leitura corrente" sobre um conjunto que inclui leitura vencida.
 *    Seria o décimo oitavo defeito da Subestação renascendo na frota.
 *
 *    `ExcluidosPorCausa` e `qualificadorDeCobertura` — o VOCABULÁRIO, com o
 *    quinto balde e a disciplina de recuar para o neutro — vêm de lá, intactos.
 *
 * ---------------------------------------------------------------------------
 * O QUE A EXAUSTIVIDADE DOS `Record` NÃO COBRE — leia antes de acrescentar um
 * nono estado a `EstadoDoCampo`.
 *
 * O cabeçalho do módulo da Subestação adverte que os `Record` cobrem
 * APRESENTAÇÃO e que a CONTABILIDADE decide por predicado, calando sobre estado
 * novo. Vale igual aqui, e estes são os pontos a percorrer À MÃO:
 *
 *   1. `ESTADOS_QUE_CONTRIBUEM` — define quem entra em soma, média e extremo.
 *      Estado novo fora dela é excluído em silêncio; dentro sem pensar, é
 *      incluído em silêncio. As duas falhas são mudas.
 *   2. `discriminarPorCausaDeCampo` — mapeia estado para os cinco baldes.
 *      Estado não mapeado desaparece da prestação de contas, e o qualificador
 *      recua para o neutro sem motivo.
 *   3. `estadoDeMovimento` — decide o badge por predicado sobre `estadoDoCampo`.
 *   4. `contagemDoCampo`, `contagemDaFrota`, `contagemDeMovimento` e
 *      `resumoDoMapa` — as partições que somam ao total. Há invariante de teste
 *      varrendo o espaço para pegar um balde que não fecha.
 *
 * NADA aqui conhece React, DOM ou fetch.
 */

import {
  cobertura,
  estadoDeLeitura,
  estaVencido,
  fonteNaoFornece,
  leituraDeIdade,
  qualificadorDeCobertura,
  temCarimboFuturo,
  temLeituraCorrente,
  valorImpossivel,
  type Cobertura,
  type ComLeitura,
  type EstadoDeLeitura,
  type ExcluidosPorCausa,
  type FreshnessCfg,
  type LeituraDatada,
  type LinhaDeFrota,
  type UnsupportedField,
} from '@nxvolt/shared';

/* ===========================================================================
 * A FORMA DO DADO
 *
 * Estrutural de propósito, pelo mesmo motivo que `frescor.ts` declara a sua:
 * o tipo do envelope publicado por `@nxvolt/shared` satisfaz estas interfaces
 * sem precisar declarar nada, e este módulo não fica acoplado ao nome do
 * envelope.
 *
 * A NULABILIDADE DE `latest` É O CONTRATO. Ela é o que torna "não existe
 * leitura" estruturalmente diferente de "a leitura é velha" — e a razão de
 * `ageSeconds` NÃO aparecer solto em nenhuma tela: para chegar à idade é
 * obrigatório passar por `latest === null` primeiro. Era exatamente o que
 * `is_active` não obrigava, e por isso `!v.is_active` dava `true` para quem
 * NUNCA reportou, com a tooltip afirmando "dados são o último snapshot
 * recebido" sobre um veículo sem snapshot nenhum.
 * ========================================================================= */

/** A leitura de telemetria de um veículo, com idade. */
export interface LeituraDeVeiculo extends LeituraDatada {
  updatedAt: string | null;
  socPct: number | null;
  rangeKm: number | null;
  energyAvailableKwh: number | null;
  cellTempAvg: number | null;
  cellTempMax: number | null;
  speedKmh: number | null;
  odometerKm: number | null;
  ignition: boolean | null;
  handbrake: boolean | null;
  gpsLat: number | null;
  gpsLon: number | null;
  gpsCourse: number | null;
  gpsSats: number | null;
  plugCharging: boolean | null;
  chargingPowerKw: number | null;
  motorTorqueNm: number | null;
  airTempOutside: number | null;
  airTempInside: number | null;
  battery12vVoltage: number | null;
}

/**
 * Um veículo visível, com identidade, procedência e (talvez) leitura.
 *
 * `msgCount` e `firstSeen` ficam FORA de `latest` porque são de
 * `signalstore_vehicles`, não de `vehicle_telemetry` — e a distinção importa:
 * eles existem mesmo em veículo que nunca reportou. A view os anula quando
 * `unsupported_fields` os inclui, e é isso que encerra o `"Mensagens: 1"` e o
 * `"Primeira msg" = agora` que rodaram quatro semanas em produção.
 */
export interface VeiculoDeFrota extends ComLeitura<LeituraDeVeiculo> {
  chassiVin: string;
  localPrefix: string | null;
  source: string;
  /** Nome da fonte para as frases NOMEAREM quem não fornece o quê. */
  sourceLabel: string;
  unsupportedFields: readonly UnsupportedField[];
  lastSeen: string | null;
  firstSeen: string | null;
  msgCount: number | null;
}

/**
 * Linha CRUA do envelope, com a idade achatada em `ageSeconds`.
 *
 * ---------------------------------------------------------------------------
 * REEXPORTADA DE `@nxvolt/shared`, E NÃO MAIS DERIVADA AQUI.
 *
 * Havia DUAS declarações desta linha — esta e a de `packages/shared/src/fleet.ts`
 * — e elas divergiram na mesma janela de tempo, em direções opostas: o front
 * modelou `ItemDaFila` como a linha ACHATADA e o servidor passou a emitir
 * `{ vehicle: LinhaDeFrota }`, ANINHADO. Os dois lados corrigiram e se
 * cruzaram. Contra o backend real `item.vehicle` era `undefined`, a projeção
 * explodia, e o portal INTEIRO caía com ela.
 *
 * Enquanto houver duas declarações do mesmo contrato elas voltam a cruzar — não
 * é questão de cuidado, é de estrutura. A definição primária é a do envelope
 * publicado; aqui ela é REEXPORTADA para os consumidores deste módulo não
 * precisarem de dois imports, e a divergência deixa de ser representável.
 *
 * Nenhuma tela deve tipar ESTADO com ela: o campo `ageSeconds` solto é o que
 * `frescor.ts` proíbe, e tirá-lo do alcance é o que impede um `Math.max(0, …)`
 * de nascer numa célula. Ela existe só para `projetarFrota` consumir.
 *
 * A PROVA de que `VeiculoDeFrota` + `LeituraDeVeiculo` cobrem o envelope campo
 * por campo é de TESTE e não de comentário — `aggregates.test.ts` constrói uma
 * linha tipada pelo envelope publicado e a passa por `projetarFrota`. Se o
 * backend acrescentar um campo de leitura e este módulo não o projetar, a
 * ausência aparece lá.
 */
export type { LinhaDeFrota };

/**
 * `undefined` vira `null`, e `null` continua `null`.
 *
 * ESTA LINHA É A CAUSA-RAIZ DE UM PORTAL EM BRANCO.
 *
 * A revisão cruzada devolveu o envelope que o servidor emitia de fato, com os
 * campos em outra grafia. Todo campo esperado saiu `undefined`, e
 * `v.localPrefix ?? v.chassiVin.slice(-6)` explodiu — `??` cobre `null` e
 * `undefined`, mas `v.chassiVin` também era `undefined` e `.slice` não existe
 * nele:
 *
 *     Uncaught TypeError: Cannot read properties of undefined (reading 'slice')
 *     document.body.innerText.length === 0
 *
 * A correção de contrato é do backend e já foi decidida (camelCase publicado em
 * `@nxvolt/shared`). ESTA correção é de FRONTEIRA e vale independentemente
 * dela: campo que não veio é AUSÊNCIA, e ausência é um dos quatro estados que
 * este módulo sabe nomear. Deixá-lo entrar como `undefined` faz o classificador
 * — cujo contrato interno é `T | null` — operar sobre um terceiro valor que ele
 * nunca modelou.
 *
 * `nulo()` não é higiene defensiva: é a mesma disciplina de `leituraDeIdade`,
 * que existe para `age_seconds NULL` não virar `ageSeconds: 0`. Uma fronteira
 * que normaliza é o que permite ao resto do módulo ter um invariante.
 */
function nulo<T>(valor: T | null | undefined): T | null {
  return valor === undefined ? null : valor;
}

/**
 * A IDENTIDADE é obrigatória e não pode ser normalizada para `null`.
 *
 * `chassiVin` é a chave: sem ele a linha não é um veículo, é ruído. Substituí-lo
 * por vazio produziria linhas que colidem em `key` de React e em `Set` de
 * discriminação, e dois "veículos" sem VIN viram um. Aqui o dado é DESCARTADO,
 * com registro no console — e `projetarFrota` conta quantos caíram para a tela
 * poder declarar, em vez de o total encolher em silêncio.
 */
function temIdentidade(l: LinhaDeFrota): boolean {
  return typeof l?.chassiVin === 'string' && l.chassiVin.length > 0;
}

/**
 * A FRONTEIRA: `age_seconds NULL` vira `latest: null`, nunca `ageSeconds: 0`.
 *
 * `leituraDeIdade` é de `frescor.ts` e o comentário de lá diz por que: zerar
 * seria o clamp de novo, com outro nome.
 */
export function projetarFrota(linhas: readonly LinhaDeFrota[]): TopologiaDeFrota {
  return topologiaDeFrota(
    (Array.isArray(linhas) ? linhas : []).filter(temIdentidade).map((l) => {
    const datada = leituraDeIdade(nulo(l.ageSeconds));
    return {
      chassiVin: l.chassiVin,
      localPrefix: nulo(l.localPrefix),
      source: nulo(l.source) ?? 'unknown',
      /* Sem rótulo de fonte a frase diria "não é fornecida por undefined". O
         genérico é verdadeiro e não inventa procedência. */
      sourceLabel: nulo(l.sourceLabel) ?? 'a fonte de telemetria ativa',
      /* Lista ausente = a fonte não declarou incapacidade nenhuma. É a
         suposição CERTA: declarar impossibilidade que ninguém afirmou seria
         inventar a informação que falta. */
      unsupportedFields: Array.isArray(l.unsupportedFields) ? l.unsupportedFields : [],
      lastSeen: nulo(l.lastSeen),
      firstSeen: nulo(l.firstSeen),
      msgCount: nulo(l.msgCount),
      latest:
        datada === null
          ? null
          : {
              ageSeconds: datada.ageSeconds,
              updatedAt: nulo(l.updatedAt),
              socPct: nulo(l.socPct),
              rangeKm: nulo(l.rangeKm),
              energyAvailableKwh: nulo(l.energyAvailableKwh),
              cellTempAvg: nulo(l.cellTempAvg),
              cellTempMax: nulo(l.cellTempMax),
              speedKmh: nulo(l.speedKmh),
              odometerKm: nulo(l.odometerKm),
              ignition: nulo(l.ignition),
              handbrake: nulo(l.handbrake),
              gpsLat: nulo(l.gpsLat),
              gpsLon: nulo(l.gpsLon),
              gpsCourse: nulo(l.gpsCourse),
              gpsSats: nulo(l.gpsSats),
              plugCharging: nulo(l.plugCharging),
              chargingPowerKw: nulo(l.chargingPowerKw),
              motorTorqueNm: nulo(l.motorTorqueNm),
              airTempOutside: nulo(l.airTempOutside),
              airTempInside: nulo(l.airTempInside),
              battery12vVoltage: nulo(l.battery12vVoltage),
            },
      };
    }),
  );
}

declare const marcaDaTopologia: unique symbol;

/**
 * A TOPOLOGIA VISÍVEL, como TIPO — a marca apontada para o outro lado.
 *
 * `BaseDeAgregados` de `frescor.ts` protege contra passar a lista CRUA onde se
 * quer a FILTRADA. A frota tem o risco inverso, e a revisão o exerceu com uma
 * linha que passou por `tsc` e por 401 testes:
 *
 *     mediaDoCampo(veiculos.filter((v) => v.latest !== null), 'soc_pct', cfg)
 *
 * O denominador vira "quem respondeu" — exatamente o quarto invariante do ADR
 * ("o denominador é a TOPOLOGIA, nunca quem respondeu") — e nada acusa, porque
 * `VeiculoDeFrota[]` filtrado continua sendo `VeiculoDeFrota[]`.
 *
 * Com a marca, o `.filter()` deixa de ser PROIBIDO e passa a ser
 * IRREPRESENTÁVEL no primeiro argumento: não compila. É a única forma que
 * sobrevive a um conjunto infinito de grafias — o comentário de
 * `BaseDeAgregados` registra quatro furos consecutivos da guarda textual
 * tentando o contrário.
 *
 * UM único produtor, `topologiaDeFrota`, e ele é a saída de `projetarFrota`:
 * quem tem a topologia é quem projetou o envelope inteiro. Um `grep` de uma
 * linha encontra a asserção; uma segunda é a regressão.
 *
 * A marca é `unique symbol` declarado e nunca emitido — some na compilação, o
 * array continua array em runtime, custo zero.
 */
export type TopologiaDeFrota = readonly VeiculoDeFrota[] & {
  readonly [marcaDaTopologia]: true;
};

/**
 * Sela uma lista como a topologia visível. ÚNICA asserção do módulo.
 *
 * Chamar isto sobre uma lista já filtrada é mentir deliberadamente, e nenhum
 * tipo impede mentira deliberada — o que ele impede é o descuido de uma
 * palavra, que é o que aconteceu. A função é exportada porque quem consome um
 * envelope que já vem filtrado pelo servidor (a linha do CCO, por exemplo)
 * precisa dizer isso explicitamente, e o nome obriga a dizer.
 */
export function topologiaDeFrota(veiculos: readonly VeiculoDeFrota[]): TopologiaDeFrota {
  return veiculos as TopologiaDeFrota;
}

/**
 * Quantas linhas a projeção DESCARTOU por não terem identidade.
 *
 * Existe para a tela poder DECLARAR o descarte. Uma frota que encolhe de 16
 * para 14 em silêncio é o defeito de denominador desta fase, com outra causa —
 * e "o servidor mandou lixo" não é desculpa para a tela mentir sobre o total.
 */
export function linhasDescartadas(linhas: readonly LinhaDeFrota[]): number {
  if (!Array.isArray(linhas)) return 0;
  return linhas.length - linhas.filter(temIdentidade).length;
}

/**
 * Configuração de frescor da frota.
 *
 * `simulated` existe porque os primitivos de `frescor.ts` o exigem — eles
 * servem também à Subestação, que TEM simulador. A frota NÃO tem: não existe
 * gerador de telemetria de veículo neste projeto, e por isso
 * `configuracaoDeFrota` fixa `simulated: false`. O estado `'neutra'` de
 * `EstadoDeLeitura` é, portanto, INALCANÇÁVEL aqui — e continua tratado em
 * todo `Record`, porque o compilador cobra e porque quem escrever um simulador
 * de frota precisa dos quatro pontos do cabeçalho percorridos à mão.
 */
export type ConfiguracaoDeFrota = FreshnessCfg;

export function configuracaoDeFrota(staleAfterSeconds: number): ConfiguracaoDeFrota {
  return { simulated: false, staleAfterSeconds };
}

/* ===========================================================================
 * EIXO 1 — apresentação do estado de leitura
 * ========================================================================= */

/**
 * O tratamento visual de cada estado de leitura — o padrão STALE do CLAUDE.md
 * num `Record` exaustivo, em vez de um ternário por tela.
 *
 * `rotulo` é o portador NÃO-CROMÁTICO (WCAG 1.4.1): a cor nunca é a única
 * portadora do significado.
 */
export const TRATAMENTO_DA_LEITURA: Record<
  EstadoDeLeitura,
  {
    /** Aplica `opacity: .55` + `grayscale(.6)` no valor. */
    degradado: boolean;
    /** Sufixo ao lado do número. `null` quando não há ressalva a fazer. */
    rotulo: string | null;
    tom: 'vivo' | 'neutro' | 'aviso' | 'perigo';
  }
> = {
  /* Inalcançável na frota (ver `configuracaoDeFrota`). Neutro e sem rótulo é o
     tratamento certo se um dia houver simulador: em simulado não se afirma
     vivacidade de número gerado. */
  neutra: { degradado: false, rotulo: null, tom: 'neutro' },
  /* Sem leitura NÃO é degradação: não há valor a degradar. Degradar o travessão
     insinuaria um snapshot que nunca existiu. */
  'sem-leitura': { degradado: false, rotulo: null, tom: 'neutro' },
  /* Carimbo futuro é PERIGO, não aviso: o dado aparenta ser o mais fresco de
     todos e não pode ser situado no tempo. */
  futura: { degradado: true, rotulo: 'carimbo à frente do relógio', tom: 'perigo' },
  vencida: { degradado: true, rotulo: 'último', tom: 'aviso' },
  corrente: { degradado: false, rotulo: null, tom: 'vivo' },
};

/**
 * A frase de idade de UM veículo, pronta.
 *
 * Existe para tirar `ageSeconds` das telas, e o motivo é o mesmo que levou
 * `textoDaIdadeDoPonto` para o módulo da Subestação: a proibição de clamp casa
 * o IDENTIFICADOR, então `Math.abs(v.latest.ageSeconds)` e um `Math.max(0, s)`
 * com alias escapam da guarda — e a grafia é infinita. Com a frase vindo
 * pronta, a tela não tem o campo para abusar.
 *
 * `formatarIdade` recebe o valor ASSINADO e é responsável por imprimir o sinal
 * (ver `fmtAge` em `components/substation/bands.ts`, que já faz isso).
 */
export function textoDaIdade(
  v: VeiculoDeFrota,
  cfg: ConfiguracaoDeFrota,
  formatarIdade: (segundos: number) => string,
): string {
  return textoDaIdadeDaLeitura(v, cfg, formatarIdade);
}

/**
 * A mesma frase sobre a forma MÍNIMA — a porta de baixo.
 *
 * Existe pelo mesmo motivo que `estadoDeValor`: o quadrante de frota da Central
 * de Operações publica uma linha achatada, não uma linha de `fleet_snapshot`.
 * Sem esta porta, aquela tela montava a frase à mão
 * (`"Sem sinal há " + fmtAge(v.ageSeconds)`), o que a obrigava a LER o campo —
 * e ler o campo na tela é o que permite o alias que derrota a guarda de texto.
 *
 * Uma frase, duas portas, zero reimplementações.
 */
export function idadeFormatadaDaLeitura(
  item: ComLeitura<LeituraDatada>,
  formatarIdade: (segundos: number) => string,
): string {
  return item.latest === null ? '—' : formatarIdade(item.latest.ageSeconds);
}

export function textoDaIdadeDaLeitura(
  item: ComLeitura<LeituraDatada>,
  cfg: ConfiguracaoDeFrota,
  formatarIdade: (segundos: number) => string,
): string {
  if (item.latest === null) return 'Nunca reportou — sem leitura para este ônibus';
  return FRASE_DA_IDADE[estadoDeLeitura(item, cfg)](formatarIdade(item.latest.ageSeconds));
}

const FRASE_DA_IDADE: Record<EstadoDeLeitura, (idade: string) => string> = {
  neutra: () => 'Dado de demonstração — sem afirmação de vivacidade',
  /* Alcançável por tipo e não por dado: `textoDaIdade` sai antes com
     `latest === null`. Fica escrito porque `Record` exaustivo cobra, e a frase
     tem de estar certa se alguém chamar o `Record` direto. */
  'sem-leitura': () => 'Nunca reportou — sem leitura para este ônibus',
  /* NÃO "há 0 s": o veículo não está fresco, está fora de hora. A frase nomeia
     a causa provável, porque é o que permite agir — fuso do
     `datalbus_sync.sql`, relógio do gateway, ou bind de `timestamptz`. */
  futura: (idade) => `Carimbo de tempo ${idade} — à frente do relógio do portal`,
  vencida: (idade) => `Sem sinal — última mensagem há ${idade}`,
  corrente: (idade) => `Atualizado há ${idade}`,
};

/**
 * Comparador de ORDENAÇÃO por idade — e a tela nunca vê o número.
 *
 * A tabela precisa ordenar por "Atualizado", e a forma óbvia era
 * `a.latest?.ageSeconds ?? null` no comparador. A guarda de texto pegou isso, e
 * está certa: a leitura do campo na tela é o que permite o alias
 * (`const idade = v.latest?.ageSeconds ?? 0`) que derrota as três regexes de
 * uma vez. Exportar um `idadeDoVeiculo(): number | null` não resolveria —
 * devolveria o número e o `?? 0` renasceria com outro nome.
 *
 * Exportar o COMPARADOR resolve: ele devolve `-1 | 0 | 1`, que não é idade, não
 * se compara com limiar e não se clampa. A guarda passa de "não escreva assim"
 * para "não há o que escrever", que é a única forma que sobrevive a renomeação.
 *
 * A ORDEM: carimbo à frente primeiro (é o mais grave e o operador precisa
 * vê-lo), depois o mais recente, e quem nunca reportou por último —
 * independentemente da direção, porque ausência não tem lugar numa escala de
 * idade. É a mesma regra de "nulo sempre no fim" das colunas numéricas.
 */
export function compararPorIdade(
  a: VeiculoDeFrota,
  b: VeiculoDeFrota,
  direcao: 1 | -1,
): number {
  const ia = a.latest === null ? null : a.latest.ageSeconds;
  const ib = b.latest === null ? null : b.latest.ageSeconds;
  if (ia === null && ib === null) return 0;
  if (ia === null) return 1;
  if (ib === null) return -1;
  return (ia - ib) * direcao;
}

/** Rótulo do veículo para telas e frases. Um só, para a mesma linha não se
 *  chamar de dois jeitos em duas dobras. */
export function rotuloDoVeiculo(v: Pick<VeiculoDeFrota, 'localPrefix' | 'chassiVin'>): string {
  return v.localPrefix ?? v.chassiVin.slice(-6);
}

/* ===========================================================================
 * EIXO 2 — SUPORTE DO CAMPO PELA FONTE
 * ========================================================================= */

/**
 * Os campos que são CÉLULA de veículo.
 *
 * `satisfies readonly UnsupportedField[]` amarra a lista à whitelist da
 * migration 0026: um typo (`cell_temp_average`) não compila. `Extract<>` NÃO
 * serviria — `Extract<'a', 'z'>` devolve `never` em silêncio, que é exatamente
 * o modo de falha que o `CHECK` do banco existe para evitar.
 *
 * FICAM DE FORA, e cada ausência é decisão:
 *  - `gps_speed_kmh` — medido `is not distinct from speed_kmh` em 16/16, porque
 *    ambos vêm de `vgpl.fleet_live.speed` (uma coluna, um sensor). Duas colunas
 *    alegando duas medições é afirmação falsa, e uma nota de rodapé dizendo
 *    "são o mesmo valor" pede ao operador o trabalho que a tela devia fazer.
 *    Some da tela, logo não é campo de célula.
 *  - `gps_ts` — o envelope publica UM carimbo por veículo, e a idade é derivada
 *    dele. Um segundo carimbo sem idade própria seria um segundo relógio sem
 *    classificador.
 *  - `soh_pct`, `gps_trail`, `performance_samples` — são RECURSO (uma curva, um
 *    rastro, uma série), não célula. Perguntam-se por `fonteForneceRecurso`.
 */
export const CAMPOS_DE_VEICULO = [
  'soc_pct',
  'range_km',
  'energy_available_kwh',
  'cell_temp_avg',
  'cell_temp_max',
  'speed_kmh',
  'odometer_km',
  'ignition',
  'handbrake',
  'gps_lat',
  'gps_lon',
  'gps_course',
  'gps_sats',
  'plug_charging',
  'charging_power_kw',
  'motor_torque_nm',
  'air_temp_outside',
  'air_temp_inside',
  'battery_12v_voltage',
  'msg_count',
  'first_seen',
] as const satisfies readonly UnsupportedField[];

export type CampoDeVeiculo = (typeof CAMPOS_DE_VEICULO)[number];

/** Recursos inteiros que uma fonte pode não sustentar. */
export const RECURSOS_DE_FROTA = [
  'soh_pct',
  'gps_trail',
  'performance_samples',
] as const satisfies readonly UnsupportedField[];

export type RecursoDeFrota = (typeof RECURSOS_DE_FROTA)[number];

/**
 * Leitura do campo, por `Record` exaustivo.
 *
 * É o ÚNICO lugar que traduz nome de campo (snake — a identidade compartilhada
 * com o `CHECK` da migration e com `unsupported_fields`) para propriedade do
 * contrato (camel). Campo novo em `CAMPOS_DE_VEICULO` não compila até ser lido
 * aqui.
 *
 * O tipo de retorno é largo de propósito: há booleanos (`ignition`), inteiros
 * (`msg_count`) e carimbos (`first_seen`), e achatar tudo em `number`
 * obrigaria a converter — o que reintroduziria a pergunta "0 significa o quê".
 */
export const LEITURA_DO_CAMPO: Record<
  CampoDeVeiculo,
  (v: VeiculoDeFrota) => number | boolean | string | null
> = {
  soc_pct: (v) => v.latest?.socPct ?? null,
  range_km: (v) => v.latest?.rangeKm ?? null,
  energy_available_kwh: (v) => v.latest?.energyAvailableKwh ?? null,
  cell_temp_avg: (v) => v.latest?.cellTempAvg ?? null,
  cell_temp_max: (v) => v.latest?.cellTempMax ?? null,
  speed_kmh: (v) => v.latest?.speedKmh ?? null,
  odometer_km: (v) => v.latest?.odometerKm ?? null,
  ignition: (v) => v.latest?.ignition ?? null,
  handbrake: (v) => v.latest?.handbrake ?? null,
  gps_lat: (v) => v.latest?.gpsLat ?? null,
  gps_lon: (v) => v.latest?.gpsLon ?? null,
  gps_course: (v) => v.latest?.gpsCourse ?? null,
  gps_sats: (v) => v.latest?.gpsSats ?? null,
  plug_charging: (v) => v.latest?.plugCharging ?? null,
  charging_power_kw: (v) => v.latest?.chargingPowerKw ?? null,
  motor_torque_nm: (v) => v.latest?.motorTorqueNm ?? null,
  air_temp_outside: (v) => v.latest?.airTempOutside ?? null,
  air_temp_inside: (v) => v.latest?.airTempInside ?? null,
  battery_12v_voltage: (v) => v.latest?.battery12vVoltage ?? null,
  /* Fora de `latest` de propósito: são de `signalstore_vehicles` e existem
     mesmo em veículo que nunca reportou. */
  msg_count: (v) => v.msgCount,
  first_seen: (v) => v.firstSeen,
};

/**
 * A GRANDEZA com o seu GÊNERO — e o gênero não é enfeite.
 *
 * ---------------------------------------------------------------------------
 * O SEXTO DEFEITO DA MESMA CLASSE, e ele passou pelo buraco do invariante.
 *
 * `ROTULO_DO_CAMPO` era `Record<CampoDeVeiculo, string>` e `FRASE_DO_CAMPO`
 * cravava o particípio no feminino:
 *
 *     'nao-suportado': (c) => `${maiuscula(c.grandeza)} não é fornecida por …`
 *
 * SETE dos vinte e um campos são masculinos, e saíam agramaticais no modal, sem
 * cenário especial nenhum:
 *
 *     "Freio de mão não é fornecida por Portal VGPL."
 *     "Torque do motor não é fornecida por Portal VGPL."
 *     "Satélites de GPS não é fornecida por Portal VGPL."
 *
 * É EXATAMENTE o "12 de 16 ônibus consideradas" de novo. E o invariante que eu
 * escrevi para aquele caso varria `qualificadorDeCobertura` contra "ônibus" —
 * NUNCA olhou o produto `FRASE_DO_CAMPO` × `ROTULO_DO_CAMPO`. Escrevi a guarda
 * contra a ocorrência e não contra a CLASSE, que é o erro que a Subestação
 * documenta em onze rodadas.
 *
 * Agora o gênero viaja com a palavra, as frases concordam por `Record`, e há
 * invariante varrendo os 21 campos × os 8 estados.
 *
 * `nome` é minúsculo e sem artigo: estas palavras entram no meio de frase, e
 * transformar uma em título é trabalho de `maiuscula`. Artigo embutido produz
 * "Não há a estimativa a exibir", que já foi corrigido em `FRASE_DE_RECURSO`
 * pela mesma razão.
 */
export type Genero = 'f' | 'm';

export interface Grandeza {
  nome: string;
  genero: Genero;
}

export const ROTULO_DO_CAMPO: Record<CampoDeVeiculo, Grandeza> = {
  soc_pct: { nome: 'estado de carga', genero: 'm' },
  range_km: { nome: 'autonomia', genero: 'f' },
  energy_available_kwh: { nome: 'energia disponível', genero: 'f' },
  cell_temp_avg: { nome: 'temperatura média de célula', genero: 'f' },
  cell_temp_max: { nome: 'temperatura máxima de célula', genero: 'f' },
  speed_kmh: { nome: 'velocidade', genero: 'f' },
  odometer_km: { nome: 'odômetro', genero: 'm' },
  ignition: { nome: 'estado da ignição', genero: 'm' },
  handbrake: { nome: 'freio de mão', genero: 'm' },
  gps_lat: { nome: 'latitude', genero: 'f' },
  gps_lon: { nome: 'longitude', genero: 'f' },
  gps_course: { nome: 'rumo', genero: 'm' },
  /* Plural masculino. O gênero governa a concordância; o número está embutido
     no nome, e "satélites de GPS não são fornecidos" sai certo porque a frase
     do estado usa a forma plural quando o nome já é plural — ver
     `CONCORDANCIA`. */
  gps_sats: { nome: 'satélites de GPS', genero: 'm' },
  plug_charging: { nome: 'conexão de carga', genero: 'f' },
  charging_power_kw: { nome: 'potência de carga', genero: 'f' },
  motor_torque_nm: { nome: 'torque do motor', genero: 'm' },
  air_temp_outside: { nome: 'temperatura externa', genero: 'f' },
  air_temp_inside: { nome: 'temperatura interna', genero: 'f' },
  battery_12v_voltage: { nome: 'tensão da bateria de 12 V', genero: 'f' },
  msg_count: { nome: 'contagem de mensagens', genero: 'f' },
  first_seen: { nome: 'data da primeira mensagem', genero: 'f' },
};

/**
 * As formas que concordam com o gênero da grandeza.
 *
 * `Record` sobre `Genero` e não um ternário no ponto de uso: um terceiro gênero
 * (nunca vai haver, mas a forma é a mesma) não compila até ser escrito, e mais
 * importante — as formas ficam TODAS num lugar, onde dá para conferir de olho
 * que nenhuma está cravada.
 */
export const CONCORDANCIA: Record<Genero, { fornecida: string; derivada: string }> = {
  f: { fornecida: 'fornecida', derivada: 'derivada' },
  m: { fornecida: 'fornecido', derivada: 'derivado' },
};

/* ===========================================================================
 * CONCORDANCIA DE NUMERO — o SETIMO defeito da mesma familia
 * ========================================================================= */

/**
 * O NUMERO, com as formas numa TABELA — e a tabela e o ponto.
 *
 * ---------------------------------------------------------------------------
 * O SETIMO DEFEITO DE CONCORDANCIA, e o primeiro que nao e de genero.
 *
 * Reproduzido ao vivo com `futuros === 1`, em cinco lugares:
 *
 *     "1 de 18 onibus TEM carimbo de tempo a frente... nenhuma leitura DESSES
 *      VEICULOS pode ser situada no tempo"          (TelemetriaFrotaPage)
 *     "1 de 18 onibus TEM carimbo a frente e NAO APARECEM no mapa"  (MapaFrotaPage)
 *     "Outros 1 ESTAO fora da janela... a posicao DELES"            (ManobristaPage)
 *
 * ---------------------------------------------------------------------------
 * POR QUE O INVARIANTE DE GENERO NAO ALCANCOU NENHUMA, e a razao importa mais
 * que as cinco ocorrencias.
 *
 * Aquele invariante varre o PRODUTO `FRASE_DO_CAMPO x ROTULO_DO_CAMPO` — duas
 * TABELAS. As cinco frases acima eram JSX INLINE nas paginas: nao havia tabela a
 * varrer, entao nao havia como a varredura chegar nelas. E o mesmo furo que o
 * comentario do sexto defeito descreve — "escrevi a guarda contra a ocorrencia e
 * nao contra a CLASSE" — um nivel acima: a classe nao e "participio cravado num
 * genero", e "TEXTO QUE CONCORDA COM CONTAGEM ESCRITO FORA DE TABELA".
 *
 * E havia o segundo furo, de modelagem: `CONCORDANCIA` modela GENERO e nao havia
 * tabela de NUMERO. Mesmo dentro das tabelas, um verbo plural cravado passaria.
 *
 * A correcao tem duas metades, e as duas sao necessarias:
 *
 *  1. ESTA TABELA, que e a de numero, irma da de genero. As formas ficam todas
 *     num lugar onde da para conferir de olho que nenhuma esta cravada.
 *  2. AS FRASES SAEM DAS PAGINAS e viram construtores aqui (`fraseDe...`), porque
 *     e o que da a varredura uma tabela para varrer. Ha guarda de texto
 *     proibindo o marcador de plural inline nas telas — "nao ha o que escrever"
 *     em vez de "nao escreva assim", que e a unica forma que sobrevive a quem
 *     escrever a sexta frase.
 *
 * O PADRAO VEIO DE UM MODULO AO LADO: `substation/aggregates.ts` ja fazia
 * `total === 1 ? 'Uma linha tem carimbo a frente...' : `${total} linhas tem...``
 * em `marcarHistorico`. Ele estava certo e nao tinha atravessado para ca.
 *
 * ---------------------------------------------------------------------------
 * "onibus" E INVARIAVEL, e e justamente por isso que ele esta na tabela.
 *
 * "1 onibus" e "18 onibus" — a palavra nao muda, e e essa invariabilidade que
 * faz o erro passar sem incomodar quem escreve: "1 de 18 onibus tem" parece
 * quase certo porque o substantivo nao protesta. Ter a forma na tabela, igual
 * nas duas colunas, registra que ela foi CONFERIDA em vez de esquecida.
 */
export type Numero = 's' | 'p';

/**
 * O numero gramatical de uma contagem.
 *
 * `Math.abs` porque contagem negativa nao existe no dominio, e se aparecer e bug
 * — e bug nao deve virar `'s'` silenciosamente por causa de `-1`. ZERO e PLURAL
 * em portugues ("0 onibus estao"), o que quase nunca aparece porque as frases de
 * contagem sao condicionadas a `> 0`; a excecao esta em `semSinalDaFrota`, e era
 * o defeito do `total === 0`.
 */
export function numeroDe(n: number): Numero {
  return Math.abs(n) === 1 ? 's' : 'p';
}

export interface FormasDeNumero {
  /** "tem" / "tem" com circunflexo — o verbo do setimo defeito, em cinco frases. */
  ter: string;
  /** "esta" / "estao" */
  estar: string;
  /** "e" / "sao" */
  ser: string;
  /** "aparece" / "aparecem" */
  aparecer: string;
  /** "reportou" / "reportaram" */
  reportar: string;
  /** "falhou" / "falharam" */
  falhar: string;
  /** INVARIAVEL nos dois — e esta aqui por isso. Ver o cabecalho. */
  onibus: string;
  /** "desse veiculo" / "desses veiculos" */
  desseVeiculo: string;
  /** "dele" / "deles" */
  dele: string;
  /** "Outro" / "Outros" — inicio de frase, dai a maiuscula. */
  outro: string;
  /** "candidato" / "candidatos" */
  candidato: string;
  /** "a ultima posicao conhecida" / "as ultimas posicoes conhecidas" */
  ultimaPosicao: string;
  /** "tentativa" / "tentativas" */
  tentativa: string;
  /**
   * "visível" / "visíveis" — e esta entrou pela NONA ocorrência do defeito.
   *
   * `{contagem.total} ônibus visíveis` no cabeçalho da tabela: com `total === 1`
   * a tela dizia "1 ônibus visíveis". Achada pela guarda NOVA — a de construto —
   * no mesmo arquivo em que as outras já tinham sido corrigidas, e invisível para
   * a guarda anterior porque "visíveis" nunca esteve em lista de verbo nenhuma.
   * É a prova de que a regra positiva alcança o que a denylist não alcançava.
   */
  visivel: string;
  /** "linha" / "linhas" */
  linha: string;
  /** "descartada" / "descartadas" — particípio, concorda em número E gênero. */
  descartada: string;
  /**
   * "vem" / "vêm" — e o "vêm" entrou pela DÉCIMA ocorrência.
   *
   * `Os ${total} ônibus visíveis vêm de X` na Saúde da Bateria: com `total === 1`
   * saíam TRÊS erros na mesma frase — artigo ("Os"), adjetivo ("visíveis") e
   * verbo ("vêm"). Nenhum deles estava em denylist nenhuma, e a frase vivia num
   * literal de template dentro de um ternário, que é onde prosa se esconde
   * melhor.
   */
  vir: string;
  /** "O" / "Os" — ARTIGO, início de frase. */
  artigo: string;
}

export const CONCORDANCIA_DE_NUMERO: Record<Numero, FormasDeNumero> = {
  s: {
    ter: 'tem',
    estar: 'está',
    ser: 'é',
    aparecer: 'aparece',
    reportar: 'reportou',
    falhar: 'falhou',
    onibus: 'ônibus',
    desseVeiculo: 'desse veículo',
    dele: 'dele',
    outro: 'Outro',
    candidato: 'candidato',
    ultimaPosicao: 'a última posição conhecida',
    tentativa: 'tentativa',
    visivel: 'visível',
    linha: 'linha',
    descartada: 'descartada',
    vir: 'vem',
    artigo: 'O',
  },
  p: {
    ter: 'têm',
    estar: 'estão',
    ser: 'são',
    aparecer: 'aparecem',
    reportar: 'reportaram',
    falhar: 'falharam',
    onibus: 'ônibus',
    desseVeiculo: 'desses veículos',
    dele: 'deles',
    outro: 'Outros',
    candidato: 'candidatos',
    ultimaPosicao: 'as últimas posições conhecidas',
    tentativa: 'tentativas',
    visivel: 'visíveis',
    linha: 'linhas',
    descartada: 'descartadas',
    vir: 'vêm',
    artigo: 'Os',
  },
};

/** Acucar: as formas que concordam com `n`. */
export function formasPara(n: number): FormasDeNumero {
  return CONCORDANCIA_DE_NUMERO[numeroDe(n)];
}

/* ===========================================================================
 * AS FRASES DE CONTAGEM DAS TELAS — aqui, e nao inline no JSX
 * ========================================================================= */

/**
 * O banner de CARIMBO A FRENTE da tabela de telemetria (`role="alert"`).
 *
 * Era JSX inline com `tem` (plural) e `desses veiculos` cravados. Ver o
 * cabecalho de `CONCORDANCIA_DE_NUMERO` para o porque de ela morar aqui.
 */
export function fraseDeCarimboFuturoNaTabela(futuros: number, total: number): string | null {
  /* Ver `parcelaDizivel`: dois numeros que nao descrevem a mesma topologia nao
     viram frase. `25 de 18 onibus tem carimbo a frente` e contradicao, nao
     medicao, e a tela nao e o lugar de publica-la. */
  if (!parcelaDizivel(futuros, total)) return null;
  const f = formasPara(futuros);
  return (
    `${futuros} de ${total} ${f.onibus} ${f.ter} carimbo de tempo à frente do relógio do ` +
    `portal. A fonte está entregando, mas o relógio dela está errado — nenhuma leitura ` +
    `${f.desseVeiculo} pode ser situada no tempo. Verifique o fuso da sincronia e o relógio ` +
    `do gateway.`
  );
}

/** O mesmo banner no Mapa ao Vivo, onde a consequencia e o pin nao aparecer. */
export function fraseDeCarimboFuturoNoMapa(futuros: number, total: number): string | null {
  if (!parcelaDizivel(futuros, total)) return null;
  const f = formasPara(futuros);
  return (
    `${futuros} de ${total} ${f.onibus} ${f.ter} carimbo à frente do relógio do portal e ` +
    `não ${f.aparecer} no mapa: a posição existe e não pode ser situada no tempo. ` +
    `Verifique o fuso da sincronia.`
  );
}

/**
 * A parte do `aria-label` do mapa que declara quantos pins existem.
 *
 * `comPosicao === 0` tem frase PROPRIA: "0 de 18 onibus tem posicao desenhada" e
 * aritmeticamente verdadeiro e pessimo como anuncio — o leitor de tela precisa
 * ouvir que o mapa esta vazio, nao fazer a conta.
 */
export function fraseDePosicaoDesenhada(comPosicao: number, total: number): string {
  /* Esta devolve STRING sempre — e o `aria-label` do mapa, e um nome acessível
     vazio é pior que um nome impreciso. Com os números incoerentes ela diz que
     não sabe, em vez de imprimir "NaN de 18 ônibus têm posição desenhada". */
  if (!parcelaDizivel(comPosicao, total)) {
    return 'Número de ônibus com posição desenhada indisponível.';
  }
  if (total === 0) return 'Nenhum ônibus visível para a fonte de telemetria ativa.';
  if (comPosicao === 0) return `Nenhum dos ${total} ônibus visíveis tem posição a desenhar.`;
  const f = formasPara(comPosicao);
  return (
    `${comPosicao} de ${total} ${f.onibus} ${f.ter} posição desenhada, ` +
    `incluindo ${f.ultimaPosicao}.`
  );
}

/**
 * A ressalva de frescor da tela do Manobrista, inteira.
 *
 * ---------------------------------------------------------------------------
 * `null` QUANDO NAO HA O QUE RESSALVAR, e as duas metades numa funcao so por
 * causa de "Outros".
 *
 * O JSX inline tinha dois blocos condicionais independentes, e o segundo abria
 * com "Outros N" — uma palavra que so faz sentido se o PRIMEIRO bloco apareceu.
 * Com `candidatosStale === 0` e `foraDaJanela > 0`, a tela dizia "Outros 3 estao
 * fora da janela" sem antecedente nenhum: outros alem de quem?
 *
 * Duas condicoes independentes produzindo uma frase que depende da outra e a
 * mesma forma do defeito de concordancia — texto composto por partes que nao
 * sabem uma da outra. Aqui a funcao ve as duas, entao "Outros" so aparece quando
 * ha antecedente, e concorda em numero quando aparece.
 */
export function fraseDeRessalvaDeFila(
  candidatosStale: number,
  foraDaJanela: number,
  total: number,
  prazoDeFrescor: string,
  janelaDeCandidatura: string,
): string | null {
  if (candidatosStale <= 0 && foraDaJanela <= 0) return null;
  const partes: string[] = [];
  if (candidatosStale > 0) {
    const f = formasPara(candidatosStale);
    partes.push(
      /*
        "DENTRO DA JANELA" NAO E "CANDIDATO A FILA" — e a frase dizia que era.

        `candidatosStale` conta quem tem posicao dentro da janela de 900 s E
        leitura vencida. Estar dentro da janela torna a posicao UTILIZAVEL;
        entrar na fila depende de SOC, sessao e raio da garagem. Medido: o
        82017 caia nesta frase como "e candidato" enquanto a mesma tela o
        punha em "Em operacao / fora do raio de 500 m".

        E a familia da fase outra vez: duas perguntas diferentes lidas como a
        mesma — exatamente o que `dentroDaJanelaDeFila` documenta ao dizer que
        candidatura e frescor sao perguntas DIFERENTES. A frase agora afirma so
        o que a contagem sustenta.
      */
      `${candidatosStale} de ${total} ${f.onibus} ${f.estar} fora do prazo de frescor ` +
        `(${prazoDeFrescor}) e com posição ainda dentro da janela de ${janelaDeCandidatura}: ` +
        `a posição ${f.dele} pode ser usada para localizar, mas não diz onde ` +
        `${candidatosStale === 1 ? 'ele está' : 'eles estão'} agora. A linha de cada um ` +
        `traz a ressalva “última posição conhecida” — posição conhecida não é ` +
        `posição atual.`,
    );
  }
  if (foraDaJanela > 0) {
    const f = formasPara(foraDaJanela);
    /* "Outros" so com antecedente. Sem ele a frase comeca pelo numero. */
    const abertura =
      candidatosStale > 0
        ? `${f.outro} ${foraDaJanela}`
        : `${foraDaJanela} de ${total} ${f.onibus}`;
    partes.push(
      `${abertura} ${f.estar} fora da janela de candidatura ou nunca ${f.reportar}, e a ` +
        `posição ${f.dele} não sustenta nenhuma afirmação sobre o pátio.`,
    );
  }
  return partes.join(' ');
}

/**
 * O VALOR DE UMA PARCELA DA TOPOLOGIA — e `null` quando a topologia e vazia.
 *
 * ---------------------------------------------------------------------------
 * O MESMO DEFEITO DO KPI "SEM SINAL", UM CARD A ESQUERDA.
 *
 * Com a frota visivel vazia, "SEM SINAL" passou a imprimir travessao — e "COM
 * SINAL 0" e "CARREGANDO 0" continuaram imprimindo zero. O comentario que
 * justificava aquela correcao dizia "os tres KPIs vizinhos imprimem travessao",
 * e ISSO ERA FALSO: so o trio de SOC degradava, porque ele passa por
 * `montarAgregado`. Os dois contadores nunca passaram por lugar nenhum — eram
 * `String(contagem.correntes)` direto no JSX.
 *
 * A distincao que importa e entre o UNIVERSO e a PARCELA:
 *
 *   "ONIBUS VISIVEIS 0" e VERDADE — e a topologia, e ela e mesmo zero.
 *   "COM SINAL 0"       e uma parcela de um conjunto vazio, e le-se como
 *                        "nenhum dos onibus esta reportando", que afirma algo
 *                        sobre onibus que nao existem.
 *
 * Zero por AUSENCIA DE FROTA nao e zero por AUSENCIA DE SINAL, e e a mesma
 * distincao que `mostraValor` faz na celula desde o comeco desta fase.
 */
/* ===========================================================================
 * O INVARIANTE DE DOIS NUMEROS — num lugar so
 * ========================================================================= */

/**
 * `parcela` e `total` descrevem a MESMA topologia?
 *
 * ---------------------------------------------------------------------------
 * EXTRAIDO PORQUE ELE ESTAVA EM UM LUGAR E FALTAVA EM CINCO.
 *
 * `valorDeParcela` ganhou o invariante e as irmas de mesma forma ficaram sem:
 *
 *     fraseDeCarimboFuturoNaTabela(25, 18) -> "25 de 18 onibus tem carimbo..."
 *     fraseDeCarimboFuturoNoMapa(25, 18)   -> "25 de 18 onibus..."
 *     fraseDePosicaoDesenhada(NaN, 18)     -> "NaN de 18 onibus tem posicao"
 *     fraseDeProcedenciaDosVisiveis(NaN, X)-> "Os NaN onibus visiveis vem de X."
 *     fraseDeLinhasDescartadas(NaN)        -> "NaN linhas descartadas"
 *
 * A terceira e o `aria-label` do mapa e a quarta e a decima ocorrencia da
 * familia. Repetir a guarda seis vezes e a forma conhecida de a setima nascer
 * sem ela — e o padrao "voce consertou uma" que este arquivo ja nomeia duas
 * vezes. Com a funcao unica, a proxima irma nasce protegida ou nao compila.
 */
export type EstadoDaParcela = 'valida' | 'indefinida' | 'contraditoria';

export function estadoDaParcela(parcela: number, total: number): EstadoDaParcela {
  if (!Number.isInteger(parcela) || !Number.isInteger(total)) return 'indefinida';
  if (parcela < 0 || total < 0) return 'indefinida';
  /* CONTRADICAO e nao ausencia: os dois numeros existem e nao podem ser os dois
     verdadeiros. Ver `valorDeParcela`. */
  if (parcela > total) return 'contraditoria';
  return 'valida';
}

/**
 * A FRASE DA CONTRADICAO — vocabulario de CONTRADICAO, nao de ausencia.
 *
 * ---------------------------------------------------------------------------
 * O CONSERTO ANTERIOR REINTRODUZIU A CONFUSAO QUE A FASE PERSEGUIU.
 *
 * `valorDeParcela(25, 18)` devolvia `null`, e `null` imprime TRAVESSAO — que
 * nesta entrega inteira significa AUSENCIA ("nao ha numero a exibir"). Mas a
 * causa aqui nao e ausencia: os dois numeros CHEGARAM e se contradizem. O
 * operador via o mesmo travessao de "a fonte nao fornece" para "o cliente e o
 * servidor discordam", que sao ações diferentes.
 *
 * E ALCANCAVEL DE VERDADE: `filas.*.length` e contado no CLIENTE e
 * `totalVisivel` vem do SERVIDOR. Reproduzido com `totalVisivel = 18` e
 * `filaCarregar = 25`.
 *
 * A tela ja tem vocabulario para isto — `inconsistencia-de-fonte`, com tom de
 * PERIGO e o valor omitido. Esta frase e a irma dele para contagem.
 */
export function fraseDeParcelaContraditoria(
  parcela: number,
  total: number,
  oQueSeConta = 'ônibus',
): string | null {
  if (estadoDaParcela(parcela, total) !== 'contraditoria') return null;
  return (
    `Contradição de contagem: esta parcela diz ${parcela} e a topologia diz ${total} ` +
    `${oQueSeConta}. Uma parcela é subconjunto do total, então um dos dois números está ` +
    `errado — o valor foi omitido porque não dá para saber qual. Verifique o serviço de ` +
    `sincronia.`
  );
}

/**
 * O VALOR DE UMA PARCELA DA TOPOLOGIA — e `null` quando nao ha numero exibivel.
 *
 * ---------------------------------------------------------------------------
 * A FAMILIA DA FASE DENTRO DA FUNCAO ESCRITA PARA MATA-LA.
 *
 * `total === 0` cobria um caso e o resto passava por `String(parcela)`, que
 * imprime qualquer coisa: `"25"` para 25 de 18, `"undefined"`, `"NaN"`, `"-3"`.
 * "25 de 18 onibus" e o SANITY OPERACIONAL do `CLAUDE.md` — estado fisicamente
 * impossivel apresentado como medicao.
 *
 * `null` AQUI SIGNIFICA "nao ha numero exibivel", e a CAUSA vem separada:
 * `fraseDeParcelaContraditoria` distingue contradicao de ausencia, porque as
 * duas pedem acoes diferentes do operador. Quem imprime o travessao tem de
 * perguntar a causa, nao supor.
 *
 * SEM `console.error` AQUI. A versao anterior logava dentro desta funcao, que
 * roda em RENDER: eram 10 linhas por montagem do Manobrista (6 KPIs x
 * StrictMode) e, com o polling de 30 s de um painel de parede, ~20 linhas por
 * minuto para sempre. O log e util e o lugar dele e um efeito, uma vez por
 * resposta — ver `ManobristaPage`.
 */
export function valorDeParcela(parcela: number, total: number): string | null {
  if (estadoDaParcela(parcela, total) !== 'valida') return null;
  return total === 0 ? null : String(parcela);
}

/**
 * DOIS NUMEROS QUE NAO DESCREVEM A MESMA TOPOLOGIA NAO VIRAM FRASE.
 *
 * O guarda-chuva das cinco irmas. `null` significa "nao ha frase a fazer", que e
 * o que todas elas ja sabem tratar.
 */
function parcelaDizivel(parcela: number, total: number): boolean {
  return estadoDaParcela(parcela, total) === 'valida';
}


/**
 * A FRASE DE UM BALDE VAZIO — e ela precisa conhecer a TOPOLOGIA.
 *
 * ---------------------------------------------------------------------------
 * "NENHUM ONIBUS AGUARDANDO RECARGA" COM ZERO ONIBUS VISIVEIS.
 *
 * Cinco das seis colunas do Manobrista escreviam afirmacao universal sobre o
 * vazio: "Nenhum onibus aguardando recarga", "Nenhum onibus carregado", "Nenhum
 * onibus em rota"... Com a frota visivel VAZIA as seis ficam vazias, e a frase
 * diz ao operador que a frota existe e esta toda fora daquele balde — quando o
 * que existe e nada.
 *
 * E o MESMO defeito do KPI "Sem sinal", que ja foi corrigido duas vezes nesta
 * fase: uma no `semSinalDaFrota` e outra em "COM SINAL"/"CARREGANDO". Na segunda
 * eu corrigi UMA das seis colunas e deixei cinco — exatamente o movimento que o
 * comentario de `valorDeParcela` descreve como o defeito anterior.
 *
 * A funcao existe para que a decisao seja tomada UMA vez: com topologia vazia a
 * frase nomeia o vazio; com topologia nao vazia ela fala do balde.
 */
export function fraseDeBaldeVazio(total: number, fraseDoBalde: string): string {
  /* MESMO INVARIANTE de `valorDeParcela`, e pela mesma razao: com `total`
     ausente ou absurdo a tela dizia "Nenhum onibus aguardando recarga", que
     afirma que a frota existe. Nao saber quantos sao nao autoriza afirmar
     sobre eles. */
  if (!Number.isInteger(total) || total <= 0) {
    return 'Nenhum ônibus visível para a fonte de telemetria ativa.';
  }
  return fraseDoBalde;
}

/**
 * O CABECALHO DA TABELA DE FROTA — a NONA ocorrencia do defeito.
 *
 * Era `{contagem.total} ônibus visíveis` inline no JSX, e com `total === 1` a
 * tela dizia "1 ônibus visíveis". Ficou de fora das cinco corrigidas porque a
 * guarda da rodada anterior era denylist de VERBO, e "visíveis" é ADJETIVO: ele
 * nunca esteve em lista nenhuma, e nunca estaria — escrever o próximo adjetivo
 * sempre foi possível.
 *
 * A guarda que o achou é a de CONSTRUTO ("substantivo contado ao lado de
 * interpolação"), que não depende de vocabulário.
 */
export function fraseDeOnibusVisiveis(total: number): string {
  /* "undefined onibus visiveis" era o que saia sem isto — no `<caption>` da
     tabela e no `aria-label` da regiao rolavel, ou seja, no NOME ACESSIVEL. */
  if (!Number.isInteger(total) || total < 0) return 'quantidade de ônibus desconhecida';
  const f = formasPara(total);
  return `${total} ${f.onibus} ${f.visivel}`;
}

/**
 * As linhas que o servidor mandou e que não viraram veículo.
 *
 * O JSX acertava o número com dois ternários inline (`linha{n === 1 ? "" : "s"}
 * descartada{n === 1 ? "" : "s"}`) — correto, e frágil pelo motivo de sempre:
 * a mesma regra escrita duas vezes na mesma linha, em duas palavras que TEM de
 * concordar entre si. A terceira cópia é a que erra.
 */
export function fraseDeLinhasDescartadas(descartadas: number): string | null {
  if (!Number.isInteger(descartadas) || descartadas <= 0) return null;
  const f = formasPara(descartadas);
  return `${descartadas} ${f.linha} ${f.descartada} por falta de identificação`;
}

/**
 * NÚMERO EM pt-BR — vírgula decimal, e o ponto estava a dois centímetros dela.
 *
 * `(item.distanceM / 1000).toFixed(1)` imprimia `"2.3 km da garagem"` na MESMA
 * linha em que a idade sai `"3,1 h"` (que passa por `fmtAge`, e esse usa
 * `toLocaleString('pt-BR')`). Ponto e vírgula decimal lado a lado, no mesmo
 * enunciado, e o `CLAUDE.md` é pt-BR.
 *
 * Não é cosmético: num painel de operação brasileiro `2.300` e `2,300` são
 * números diferentes, e a dúvida cai justamente sobre distância e energia.
 */
export function numeroPtBr(valor: number, casas: number): string {
  if (!Number.isFinite(valor)) return '—';
  /*
    `-0` É A ARMADILHA NOMEADA DESTE PROJETO, e ela caiu na função nova.

    `(-0).toLocaleString('pt-BR')` devolve `"-0,0"`, e a tela imprimia
    `"-0,0 km da gar" + "agem"`. Um sinal de menos num número que é zero lê-se
    como erro de dado onde não há erro nenhum.

    `Object.is(-0, 0)` é `false`, então `valor === 0` JÁ casa os dois — mas
    `Number.isInteger(-0)` é `true` e `expect(-0).not.toBe(0)` PASSA, o que faz o
    caso atravessar guarda e teste ao mesmo tempo. Somar zero normaliza.
  */
  const normalizado = valor === 0 ? 0 : valor;
  return normalizado.toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

/**
 * A PROCEDENCIA DOS VISIVEIS — a DECIMA ocorrencia do defeito.
 *
 * Era um literal de template dentro de um ternario, na Saude da Bateria:
 *
 *     `Os ${totalDeVeiculos} ônibus visíveis vêm de ${fontes}.`
 *
 * Com `total === 1`: "Os 1 ônibus visíveis vêm de Portal VGPL" — artigo, adjetivo e
 * verbo errados na MESMA frase. Nenhuma denylist a alcancaria: "Os" e artigo,
 * "visíveis" e adjetivo, "vêm" e um verbo que ninguem teria listado.
 *
 * A guarda de CONSTRUTO a achou porque "onibus" estava ao lado de `${...}`, que
 * e a unica coisa que as dez ocorrencias tem em comum.
 *
 * No singular a frase DISPENSA o numero: "O onibus visivel vem de X" diz o mesmo
 * que "O 1 onibus visivel", e diz em portugues.
 */
export function fraseDeProcedenciaDosVisiveis(total: number, fontes: string): string {
  /* A DECIMA ocorrência da família, e ela saía "Os NaN ônibus visíveis vêm de
     Portal VGPL." — dentro de um literal de template num ternário, que é onde prosa
     se esconde melhor. `total` aqui é a própria topologia, então a parcela é ela
     mesma: o invariante vira "é inteiro não negativo". */
  if (!parcelaDizivel(total, total)) {
    return `A procedência dos ônibus visíveis não pôde ser determinada (${fontes}).`;
  }
  const f = formasPara(total);
  const sujeito =
    total === 1
      ? `${f.artigo} ${f.onibus} ${f.visivel}`
      : `${f.artigo} ${total} ${f.onibus} ${f.visivel}`;
  return `${sujeito} ${f.vir} de ${fontes}.`;
}

/**
 * O RECORTE DECLARADO da tabela do CCO.
 *
 * `null` quando nao ha recorte a declarar. O LIMITE e parametro e nao literal
 * porque ele estava escrito DUAS vezes na tela — no `slice(0, 12)` e na frase
 * "os 12 primeiros" — e duas redacoes do mesmo numero e o mecanismo que este
 * projeto documenta ha onze rodadas.
 *
 * A frase so aparece com `total > limite`, entao o plural e garantido pela
 * guarda; ela mora aqui mesmo assim, porque a regra da tela e que prosa com
 * contagem nao se escreve inline — e "garantido pela guarda" e exatamente o
 * tipo de garantia que a proxima edicao remove sem perceber.
 */
export function fraseDeRecorteDaTabela(total: number, limite: number): string | null {
  if (total <= limite) return null;
  const f = formasPara(total);
  return `Mostrando os ${limite} primeiros de ${total} ${f.onibus} nesta resposta.`;
}

/**
 * A PARTICAO QUE NAO FECHA — a DECIMA PRIMEIRA ocorrencia.
 *
 * O Manobrista verifica, a cada render, se as seis colunas somam o total que o
 * servidor informa, e grita quando nao somam. A verificacao esta certa e e uma
 * das melhores coisas da tela. A FRASE dela era inline:
 *
 *     as colunas somam {soma} e o servidor informa {total} onibus visiveis.
 *
 * Com `total === 1`: "1 onibus visiveis". O aviso que existe para denunciar
 * incoerencia era, ele proprio, incoerente — e so aparece no caso raro, que e
 * onde ninguem olha.
 *
 * `null` quando a particao fecha: e o caso normal, e nao ha o que dizer.
 */
export function fraseDeParticaoQueNaoFecha(soma: number, total: number): string | null {
  if (soma === total) return null;
  const f = formasPara(total);
  return (
    `Atenção: as colunas somam ${soma} e o servidor informa ${total} ${f.onibus} ` +
    `${f.visivel}. Há ônibus fora de todas as colunas desta tela.`
  );
}

/**
 * A FALHA DE ATUALIZACAO DO PORTAL — vocabulario que nao existia.
 *
 * ---------------------------------------------------------------------------
 * "A FONTE PAROU" E "O PORTAL PAROU DE BUSCAR" SAO COISAS DIFERENTES.
 *
 * Medido na revisao: com todo `fetch` rejeitando por 35 s, o carimbo de
 * atualizacao congela — o que esta CERTO, porque o dado e mesmo o da ultima
 * resposta — e nao havia aviso nenhum. A entrega tem vocabulario rico para "a
 * FONTE parou" (sem sinal, carimbo a frente, nunca reportou) e nenhum para "o
 * PORTAL parou de buscar".
 *
 * A diferenca e operacional, nao cosmetica: no primeiro caso o operador vai
 * olhar o gateway; no segundo, a rede dele ou a sessao. E enquanto ninguem diz
 * nada, a tela inteira — inclusive os badges "Rodando" e os KPIs de leitura
 * corrente — continua afirmando o AGORA sobre uma resposta que envelhece.
 *
 * A idade vem como texto JA FORMATADO pelo chamador (`fmtAge`), pelo mesmo
 * motivo dos outros construtores: a formatacao de idade existe num lugar so.
 */
export function fraseDeAtualizacaoParada(
  tentativas: number,
  idadeDoUltimoSucesso: string | null,
): string {
  const f = formasPara(tentativas);
  const desde =
    idadeDoUltimoSucesso === null
      ? 'e esta tela nunca conseguiu carregar'
      : `e a última resposta bem-sucedida tem ${idadeDoUltimoSucesso}`;
  return (
    `O portal parou de buscar dados: ${tentativas} ${f.tentativa} de atualização ` +
    `${f.falhar} ${desde}. Não é a fonte que parou — é a busca do portal que está ` +
    `falhando, e por isso os números desta tela não mudam mais e vão envelhecendo sem ` +
    `aviso. Recarregue a página; se persistir, verifique a sua conexão e se a sessão ` +
    `ainda está aberta.`
  );
}

/* `ROTULO_DO_RECURSO` foi REMOVIDO. Ele existia para ser interpolado num
   template único de frase de recurso, e foi exatamente esse template que
   produziu "Não há a estimativa de saúde da bateria a exibir" — artigo
   duplicado, porque o rótulo trazia artigo. Com `FRASE_DE_RECURSO` tendo uma
   frase por recurso, o rótulo solto não tem consumidor e manter um export sem
   uso é convidar a próxima interpolação. */

/**
 * A fonte deste veículo fornece o campo? Pergunta do EIXO 2, isolada.
 *
 * Delega a `fonteNaoFornece` de `frescor.ts`, que é o único lugar do projeto
 * que interpreta `unsupported_fields`. Não reimplementar aqui: uma segunda
 * leitura da lista é uma segunda verdade.
 */
export function fonteForneceCampo(
  v: Pick<VeiculoDeFrota, 'unsupportedFields'>,
  campo: CampoDeVeiculo,
): boolean {
  return !fonteNaoFornece(v.unsupportedFields, campo);
}

/** Mesma pergunta para um RECURSO (curva de SOH, rastro, série de desempenho). */
export function fonteForneceRecurso(
  unsupportedFields: readonly UnsupportedField[] | null | undefined,
  recurso: RecursoDeFrota,
): boolean {
  return !fonteNaoFornece(unsupportedFields, recurso);
}

/* ===========================================================================
 * O PRODUTO DOS DOIS EIXOS — o classificador ÚNICO
 * ========================================================================= */

export type EstadoDoCampo =
  /** Eixo 2: a fonte não fornece, e o dado veio nulo — coerente. */
  | 'nao-suportado'
  /**
   * Eixo 2 VIOLADO: a fonte declara não fornecer e o banco trouxe VALOR. É a
   * última célula da matriz, e é bug de dado — ou `unsupported_fields` está
   * errado, ou algo escreve o que não devia. Tem de APARECER: engolir a
   * contradição é como `msg_count = 1` sobreviveu quatro semanas em produção.
   */
  | 'inconsistencia-de-fonte'
  /** Eixo 1: nunca reportou. */
  | 'sem-leitura'
  /** Eixo 1: carimbo à frente do relógio. Domina o valor, se houver. */
  | 'futura'
  /** Vencida, COM valor: degrada e ganha "(último)". */
  | 'vencida'
  /**
   * Vencida e SEM o valor. Estado próprio porque "(último)" ao lado de um
   * travessão é ressalva sem grandeza — e ressalva sem grandeza não ressalva
   * nada. É o par que testa o classificador inteiro: o `soc_pct` ausente em 2
   * de 16 é AUSÊNCIA, enquanto o `range_km` na coluna ao lado é
   * IMPOSSIBILIDADE, e os dois imprimiriam o mesmo travessão se o estado fosse
   * um só.
   */
  | 'vencida-sem-grandeza'
  /** Corrente, campo nulo: "leitura sem <grandeza>". */
  | 'sem-grandeza'
  /** Corrente, com valor. O único estado em que o número é do AGORA. */
  | 'corrente';

/**
 * O CLASSIFICADOR. Nenhuma tela decide isto por conta própria.
 *
 * ORDEM DOS TESTES, e aqui ela IMPORTA de verdade — diferente de
 * `estadoDeLeitura`, onde já se escreveu que importava e a mutação provou que
 * não:
 *
 *  1. EIXO 2 PRIMEIRO. Impossibilidade não é um estado de leitura: vale para os
 *     16 veículos ao mesmo tempo, INCLUSIVE para o que nunca reportou. Testar a
 *     idade antes faria o veículo sem linha de telemetria sair "sem leitura"
 *     numa coluna que a fonte é incapaz de preencher — trocando
 *     impossibilidade por ausência, que é a confusão que esta fase persegue.
 *     É a mesma precedência que `discriminarPorCausa` de `frescor.ts` adota, e
 *     pela mesma razão escrita lá.
 *  2. `inconsistencia-de-fonte` dentro do eixo 2: a contradição é entre a
 *     declaração da fonte e o valor, e não depende de idade nenhuma. É a mesma
 *     conjunção que `valorImpossivel` de `frescor.ts` expressa — ver
 *     `estadoDeValor`, que a escreve com a metade da lista já consumida.
 *  3. `sem-leitura` antes de `futura` e `vencida`: sem carimbo não há idade a
 *     classificar.
 *  4. `futura` antes de `vencida`: mutuamente exclusivos (`age < -5` e
 *     `age > 120` não se sobrepõem), então a ordem é legibilidade e não
 *     necessidade. Fica registrado como é de fato.
 */
export function estadoDoCampo(
  v: VeiculoDeFrota,
  campo: CampoDeVeiculo,
  cfg: ConfiguracaoDeFrota,
): EstadoDoCampo {
  return estadoDeValor(
    v,
    fonteForneceCampo(v, campo),
    LEITURA_DO_CAMPO[campo](v),
    cfg,
  );
}

/**
 * O CORPO do classificador, sobre a forma MÍNIMA.
 *
 * Existe porque há um segundo consumidor com uma linha diferente: o quadrante de
 * frota da Central de Operações publica `prefixo`, `socPct`, `potenciaKw`,
 * `ageSeconds`, `sourceLabel` e `unsupportedFields` — e não a linha inteira de
 * `fleet_snapshot`. Sem esta extração o CCO precisaria reescrever a
 * classificação, e "o mesmo critério em duas grafias" é o mecanismo que produziu
 * 18 defeitos na Subestação.
 *
 * `estadoDoCampo` é a porta de cima (nomeia o campo, lê o valor e o suporte por
 * `Record`); esta é a porta de baixo (recebe os três já resolvidos). O CORPO —
 * a ordem dos testes e as decisões — é UM.
 */
export function estadoDeValor(
  item: ComLeitura<LeituraDatada>,
  suportado: boolean,
  valor: number | boolean | string | null,
  cfg: ConfiguracaoDeFrota,
): EstadoDoCampo {
  /* O suporte chega como BOOLEANO, já resolvido pelo chamador — e a leitura de
     `unsupported_fields` continua existindo em UM lugar só, `fonteNaoFornece` de
     `frescor.ts`, alcançado por `fonteForneceCampo`. O `grep` de uma linha que o
     comentário de lá promete continua valendo.
     A contradição é a conjunção, escrita por extenso porque a metade da lista já
     foi consumida: fonte declara não fornecer E veio valor. */
  if (!suportado) return valor !== null ? 'inconsistencia-de-fonte' : 'nao-suportado';
  const leitura = estadoDeLeitura(item, cfg);
  if (leitura === 'sem-leitura') return 'sem-leitura';
  /* Carimbo futuro DOMINA o valor: uma leitura que não se pode situar no tempo
     não fica melhor por trazer a grandeza. Colapso deliberado — não existe
     'futura-sem-grandeza', porque a ação do operador é a mesma nos dois casos
     (conferir fuso da coleta e relógio do gateway) e a distinção não mudaria
     nada na tela nem na conta. */
  if (leitura === 'futura') return 'futura';
  const temValor = valor !== null;
  if (leitura === 'vencida') return temValor ? 'vencida' : 'vencida-sem-grandeza';
  return temValor ? 'corrente' : 'sem-grandeza';
}

/**
 * Estados em que EXISTE valor exibível.
 *
 * `vencida` está dentro: o padrão STALE do CLAUDE.md manda DEGRADAR o valor,
 * não apagá-lo — trocar "sem sinal" por "sem frota" é a mentira pior, e é
 * exatamente o que os cortes de 600 s e 900 s na SQL faziam.
 */
const ESTADOS_COM_VALOR: readonly EstadoDoCampo[] = ['corrente', 'vencida'];

/**
 * Estados que CONTRIBUEM para soma, média e extremo. Ponto 1 da lista do
 * cabeçalho.
 *
 * É a mesma lista de `ESTADOS_COM_VALOR`, e a coincidência é decisão: o
 * agregado inclui o valor vencido e DECLARA quantos são (`vencidosUsados`), em
 * vez de excluí-lo. Excluir esvaziaria os KPIs com 10 de 16 veículos parados
 * na origem — e KPI vazio ao lado de tabela cheia é o décimo defeito da
 * Subestação ("a ausência deixou de eleger o KPI errado e passou a APAGAR os
 * certos").
 *
 * `futura` fica FORA, por motivo diferente de todos os outros: não é que o
 * valor seja velho, é que ele não é situável no tempo. Incluí-lo faria o
 * carimbo à frente parecer a contribuição mais atual do conjunto.
 *
 * `inconsistencia-de-fonte` fica fora porque o número não é de confiança.
 */
const ESTADOS_QUE_CONTRIBUEM: readonly EstadoDoCampo[] = ESTADOS_COM_VALOR;

/**
 * O valor do campo QUANDO ELE PODE SER LIDO, e `null` em qualquer outro caso.
 *
 * É o acessador que as telas usam em vez de `LEITURA_DO_CAMPO`, e a diferença é
 * o ponto: aqui o valor de um campo em `inconsistencia-de-fonte` sai `null`, de
 * modo que nenhuma célula, soma ou média possa apresentar como medição um
 * número que a própria fonte declara impossível.
 */
export function valorUtilizavel(
  v: VeiculoDeFrota,
  campo: CampoDeVeiculo,
  cfg: ConfiguracaoDeFrota,
): number | boolean | string | null {
  return ESTADOS_COM_VALOR.includes(estadoDoCampo(v, campo, cfg))
    ? LEITURA_DO_CAMPO[campo](v)
    : null;
}

/** Numérico, para soma e média. `null` quando o campo não é número finito. */
export function numeroUtilizavel(
  v: VeiculoDeFrota,
  campo: CampoDeVeiculo,
  cfg: ConfiguracaoDeFrota,
): number | null {
  const valor = valorUtilizavel(v, campo, cfg);
  return typeof valor === 'number' && Number.isFinite(valor) ? valor : null;
}

/* ===========================================================================
 * AS FRASES — `Record` exaustivos sobre `EstadoDoCampo`
 * ========================================================================= */

export interface ContextoDaFrase {
  /** Nome da grandeza, minúsculo (`ROTULO_DO_CAMPO[campo].nome`). */
  grandeza: string;
  /**
   * O GÊNERO da grandeza. Está no contexto, e não resolvido antes, porque é a
   * FRASE que decide se precisa concordar — sete das oito não precisam, e
   * obrigar o chamador a pré-concordar seria espalhar a regra.
   */
  genero: Genero;
  /** `sourceLabel`, para a frase NOMEAR quem falta. */
  fonte: string;
  /** Idade já formatada, com sinal. */
  idade: string;
}

/**
 * A frase por estado. Vai para `title`, `aria-label` e célula.
 *
 * Nenhuma delas tem "ainda". O advérbio promete que esperar resolve, e em
 * `nao-suportado` isso é falso: o Portal VGPL não entrega temperatura de célula
 * hoje nem amanhã.
 */
export const FRASE_DO_CAMPO: Record<EstadoDoCampo, (c: ContextoDaFrase) => string> = {
  /* A ÚNICA das oito que concorda com o gênero da grandeza — as outras sete
     não têm particípio nem adjetivo referindo-se a ela, e por isso atravessam
     sem tocar. O invariante varre as oito mesmo assim: qual delas precisa de
     concordância é consequência da redação, e redação muda. */
  'nao-suportado': (c) =>
    `${maiuscula(c.grandeza)} não é ${CONCORDANCIA[c.genero].fornecida} por ${c.fonte}.`,
  'inconsistencia-de-fonte': (c) =>
    `Inconsistência de fonte: ${c.fonte} declara não fornecer ${c.grandeza}, e a leitura trouxe valor. O número não é confiável.`,
  'sem-leitura': () => 'Este ônibus está cadastrado e nunca reportou — não há leitura nem snapshot.',
  futura: (c) =>
    `Carimbo de tempo ${c.idade}, à frente do relógio do portal: a leitura não pode ser situada no tempo. Verifique o fuso da coleta e o relógio do gateway.`,
  vencida: (c) =>
    `Sem sinal há ${c.idade} — ${c.grandeza} é o último valor recebido, não o de agora.`,
  'vencida-sem-grandeza': (c) =>
    `Sem sinal há ${c.idade}, e a última leitura não trouxe ${c.grandeza}.`,
  'sem-grandeza': (c) => `Leitura corrente sem ${c.grandeza}.`,
  corrente: (c) => `${maiuscula(c.grandeza)} em leitura corrente.`,
};

/**
 * O tratamento visual por estado de campo. Um `Record`, não um ternário por
 * célula — e `mostraValor` é o que impede "0,0 km" de significar "sem dado".
 */
export const TRATAMENTO_DO_CAMPO: Record<
  EstadoDoCampo,
  {
    /** A célula imprime o número? `false` -> imprime o travessão. */
    mostraValor: boolean;
    /**
     * Degradar visualmente o valor. O COMO mora em `FleetCells`
     * (`ESTILO_DEGRADADO`), e ele deixou de usar `opacity`: a composicao
     * `opacity: .55` + `grayscale(.6)` media 3,16:1 sobre o valor, que nao tem
     * outro portador na celula. Ver o cabecalho de `ESTILO_DEGRADADO`.
     */
    degradado: boolean;
    /** Sufixo dentro da célula. Portador não-cromático. */
    sufixo: string | null;
    tom: 'vivo' | 'neutro' | 'aviso' | 'perigo';
  }
> = {
  'nao-suportado': { mostraValor: false, degradado: false, sufixo: 'não fornecido', tom: 'neutro' },
  'inconsistencia-de-fonte': {
    mostraValor: false,
    degradado: false,
    sufixo: 'inconsistência de fonte',
    tom: 'perigo',
  },
  'sem-leitura': { mostraValor: false, degradado: false, sufixo: null, tom: 'neutro' },
  /* O valor NÃO é mostrado com carimbo futuro: apresentá-lo, mesmo degradado,
     é apresentar como medição algo que não tem quando. */
  futura: { mostraValor: false, degradado: false, sufixo: 'carimbo à frente', tom: 'perigo' },
  vencida: { mostraValor: true, degradado: true, sufixo: 'último', tom: 'aviso' },
  /* Sem grandeza: sufixo NENHUM. "(último)" ao lado de "—" é ressalva sem
     grandeza, e ressalva sem grandeza não ressalva nada. */
  'vencida-sem-grandeza': { mostraValor: false, degradado: false, sufixo: null, tom: 'aviso' },
  'sem-grandeza': { mostraValor: false, degradado: false, sufixo: null, tom: 'neutro' },
  corrente: { mostraValor: true, degradado: false, sufixo: null, tom: 'vivo' },
};

/** A célula pronta: o que imprimir, como degradar, e a frase que explica. */
export interface CelulaDeCampo {
  estado: EstadoDoCampo;
  /** `null` quando o estado não autoriza imprimir número. */
  valor: number | boolean | string | null;
  mostraValor: boolean;
  degradado: boolean;
  sufixo: string | null;
  tom: 'vivo' | 'neutro' | 'aviso' | 'perigo';
  /** Vai para `title` e entra no `aria-label`. */
  frase: string;
}

/**
 * A célula de um campo, pronta.
 *
 * Existe pelo mesmo motivo que `KpiDaSubestacao` atravessa a fronteira como um
 * objeto: com estado, valor e frase soltos, a revisão cruzada recompôs o valor
 * a partir do dado cru três vezes na Subestação — e o resultado é pior que o
 * defeito original, porque a tela passa a ATESTAR uma procedência que o número
 * não tem. Aqui não sobra nada para recompor.
 */
export function celulaDeCampo(
  v: VeiculoDeFrota,
  campo: CampoDeVeiculo,
  cfg: ConfiguracaoDeFrota,
  formatarIdade: (segundos: number) => string,
): CelulaDeCampo {
  const estado = estadoDoCampo(v, campo, cfg);
  const t = TRATAMENTO_DO_CAMPO[estado];
  return {
    estado,
    valor: t.mostraValor ? LEITURA_DO_CAMPO[campo](v) : null,
    mostraValor: t.mostraValor,
    degradado: t.degradado,
    sufixo: t.sufixo,
    tom: t.tom,
    frase: FRASE_DO_CAMPO[estado]({
      grandeza: ROTULO_DO_CAMPO[campo].nome,
      genero: ROTULO_DO_CAMPO[campo].genero,
      fonte: v.sourceLabel,
      idade: v.latest === null ? '—' : formatarIdade(v.latest.ageSeconds),
    }),
  };
}

/** Primeira letra em caixa alta. As grandezas nascem minúsculas porque quase
 *  sempre entram no meio de frase. */
export function maiuscula(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** Lista em português: "a", "a e b", "a, b e c". */
export function listar(itens: readonly string[]): string {
  if (itens.length === 0) return '';
  if (itens.length === 1) return itens[0] as string;
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1] as string}`;
}

/* ===========================================================================
 * IMPOSSIBILIDADE NO NÍVEL DA COLUNA (§6.1)
 * ========================================================================= */

/**
 * A coluna some da tabela?
 *
 * CINCO colunas ficam permanentemente vazias com o Portal VGPL (`range_km`,
 * `energy_available_kwh`, `cell_temp_avg`, `cell_temp_max`,
 * `charging_power_kw`). Cinco travessões lado a lado ENSINAM o operador a
 * ignorar travessão — e aí o travessão legítimo dos 2 veículos sem SOC morre
 * junto. A coluna sai, e uma nota de cabeçalho nomeia a fonte e o que ela não
 * fornece.
 *
 * `veiculos.length > 0` é guarda, não higiene: `[].every(...)` é `true`, e sem
 * ela uma frota vazia esconderia TODAS as colunas — estado vazio virando
 * afirmação sobre capacidade de fonte.
 */
export function campoOculto(veiculos: TopologiaDeFrota, campo: CampoDeVeiculo): boolean {
  if (veiculos.length === 0) return false;
  if (!veiculos.every((v) => !fonteForneceCampo(v, campo))) return false;
  /*
   * A COLUNA NÃO SOME SE HOUVER CONTRADIÇÃO DENTRO DELA.
   *
   * `campoOculto` decidia só por `fonteForneceCampo`, então a coluna sumia
   * MESMO quando alguma linha trazia valor num campo declarado impossível. A
   * revisão contou as células em `inconsistencia-de-fonte` renderizadas na
   * tabela: ZERO. O banner de inconsistência apontava para uma coluna que não
   * existia, e a "última célula da matriz" — que o cabeçalho deste módulo diz
   * que TEM DE APARECER — era inobservável.
   *
   * Esconder é uma otimização de leitura: vale quando a coluna só teria
   * travessões. Com uma contradição dentro ela deixa de ser uma coluna vazia e
   * passa a ser a evidência de um bug de dado — e evidência não se esconde por
   * conforto visual.
   */
  return !veiculos.some((v) =>
    valorImpossivel(v.unsupportedFields, campo, LEITURA_DO_CAMPO[campo](v)),
  );
}

/** As fontes (por rótulo, sem repetir) que não fornecem o campo. */
export function fontesSemCampo(
  veiculos: TopologiaDeFrota,
  campo: CampoDeVeiculo,
): string[] {
  return [
    ...new Set(veiculos.filter((v) => !fonteForneceCampo(v, campo)).map((v) => v.sourceLabel)),
  ].sort();
}

/**
 * A nota de cabeçalho que presta contas das colunas omitidas.
 *
 * Sem ela, esconder a coluna trocaria uma mentira (o travessão lido como
 * ausência) por um apagamento (a capacidade desapareceu sem explicação) — e o
 * ADR é explícito sobre isso ao recusar esconder o módulo Saúde da Bateria:
 * "esconder apaga a evidência de que a capacidade existia e foi perdida na
 * troca de fonte".
 */
export function notaDeCamposOcultos(
  veiculos: TopologiaDeFrota,
  campos: readonly CampoDeVeiculo[],
): string | null {
  const ocultos = campos.filter((campo) => campoOculto(veiculos, campo));
  if (ocultos.length === 0) return null;
  const quem = listar([
    ...new Set(ocultos.flatMap((campo) => fontesSemCampo(veiculos, campo))),
  ].sort());
  const grandezas = listar(ocultos.map((campo) => ROTULO_DO_CAMPO[campo].nome));
  return ocultos.length === 1
    ? `A coluna de ${grandezas} foi omitida: ${quem} não fornece esse dado para nenhum ônibus visível.`
    : `${ocultos.length} colunas foram omitidas — ${grandezas} — porque ${quem} não fornece esses dados para nenhum ônibus visível.`;
}

/* ===========================================================================
 * OS AGREGADOS
 * ========================================================================= */

/**
 * Classifica cada excluído pela causa, pelo MESMO `estadoDoCampo` da célula.
 * Ponto 2 da lista do cabeçalho. Ver a divergência 2 do cabeçalho para saber
 * por que isto não é `discriminarPorCausa` de `frescor.ts`.
 */
export function discriminarPorCausaDeCampo(
  veiculos: TopologiaDeFrota,
  campo: CampoDeVeiculo,
  cfg: ConfiguracaoDeFrota,
): ExcluidosPorCausa {
  const fora = veiculos.filter(
    (v) => !ESTADOS_QUE_CONTRIBUEM.includes(estadoDoCampo(v, campo, cfg)),
  );
  const por = (...alvos: EstadoDoCampo[]) =>
    fora.filter((v) => alvos.includes(estadoDoCampo(v, campo, cfg))).length;
  return {
    semLeitura: por('sem-leitura'),
    /* Ninguém é excluído POR ESTAR VENCIDO — o vencido contribui e é declarado
       em `vencidosUsados`. O balde fica em zero de propósito: o qualificador
       não pode dizer "com leitura corrente" sobre um conjunto que INCLUI
       leitura vencida. É a divergência 2 do cabeçalho, materializada. */
    vencidos: 0,
    carimboFuturo: por('futura'),
    semGrandeza: por('sem-grandeza', 'vencida-sem-grandeza'),
    /* Impossibilidade e contradição no MESMO balde: as duas dizem "a fonte não
       devia ter isto". A contradição é contada à parte em `inconsistencias`,
       para a tela poder gritar. */
    naoSuportado: por('nao-suportado', 'inconsistencia-de-fonte'),
  };
}

export interface AgregadoDeFrota {
  /** `null` quando nenhum veículo contribuiu. NUNCA 0 — zero é um número que o
   *  operador acredita. */
  valor: number | null;
  contribuintes: number;
  /** DENOMINADOR = a topologia visível, nunca quem respondeu. */
  total: number;
  excluidos: ExcluidosPorCausa;
  /** Quantos dos contribuintes estão VENCIDOS. `> 0` obriga o consumidor a
   *  vestir o chrome de "último" no número. */
  vencidosUsados: number;
  /** Campo declarado impossível que chegou com valor. `> 0` é bug de dado. */
  inconsistencias: number;
  /** A frase, pronta, com "M de N" e o qualificador. */
  frase: string;
}

type FraseDeCobertura = Record<Cobertura, (c: ContagemDeCobertura) => string>;

interface ContagemDeCobertura {
  contribuintes: string;
  total: string;
  label: string;
  grandeza: string;
  /* Carregado mesmo sem nenhum dos três vocabulários usá-lo hoje: as frases de
     cobertura não têm particípio referindo-se à grandeza. Está aqui para que a
     próxima redação tenha o gênero na mão em vez de cravar uma forma — foi
     cravar que produziu o sexto defeito desta classe. */
  genero: Genero;
  qualificador: string;
}

/* Um classificador (`cobertura`), N vocabulários: cada agregado tem o seu
   `Record`, e todos herdam a exaustividade. */

const FRASE_DA_MEDIA: FraseDeCobertura = {
  unica: (c) => `Único ônibus visível, com ${c.grandeza}`,
  todas: (c) => `Média dos ${c.total} ônibus visíveis`,
  parcial: (c) => `Média de ${c.contribuintes} de ${c.total} ônibus ${c.qualificador}`,
  /* 1 de N NÃO é "média": média de um é o próprio valor, e chamar de média
     esconde que o conjunto tem um elemento. */
  'uma-de-varias': (c) => `Somente ${c.label} · 1 de ${c.total} ônibus ${c.qualificador}`,
  nenhuma: (c) => `Nenhum dos ${c.total} ônibus visíveis com ${c.grandeza}`,
};


const FRASE_DO_EXTREMO: FraseDeCobertura = {
  unica: (c) => `Único ônibus visível, com ${c.grandeza}`,
  todas: (c) => `Entre os ${c.total} ônibus visíveis`,
  parcial: (c) => `Entre ${c.contribuintes} de ${c.total} ônibus ${c.qualificador}`,
  'uma-de-varias': (c) => `Somente ${c.label} · 1 de ${c.total} ônibus ${c.qualificador}`,
  nenhuma: (c) => `Nenhum dos ${c.total} ônibus visíveis com ${c.grandeza}`,
};

/**
 * A frase do agregado.
 *
 * DUAS SAÍDAS ANTES do classificador de cobertura, e as duas são dependência de
 * ordem registrada porque `cobertura()` não sabe de nenhuma das duas — do mesmo
 * jeito que o comentário dela adverte sobre `total === 0`:
 *
 *  1. `total === 0` — `cobertura(0, 0)` devolve `'nenhuma'`, cuja frase
 *     ("Nenhum dos 0 ônibus visíveis com autonomia") implicaria existir ônibus
 *     mudo. Não há frota, e a frase é sobre isso.
 *  2. TODO excluído é `naoSuportado` — a frase de cobertura diria "nenhum dos
 *     16 com autonomia", que é verdade e é a verdade ERRADA: sugere que os 16
 *     deixaram de mandar. A causa é IMPOSSIBILIDADE, e é a coluna direita da
 *     matriz colapsando — a economia que justifica o eixo separado.
 */
function fraseDoAgregado(
  vocabulario: FraseDeCobertura,
  contribuintes: readonly VeiculoDeFrota[],
  veiculos: TopologiaDeFrota,
  campo: CampoDeVeiculo,
  excluidos: ExcluidosPorCausa,
): string {
  const total = veiculos.length;
  const { nome, genero } = ROTULO_DO_CAMPO[campo];
  if (total === 0) return 'Nenhum ônibus visível';
  if (contribuintes.length === 0) {
    return motivoSemContribuinte(excluidos, total, campo, veiculos);
  }
  return vocabulario[cobertura(contribuintes.length, total)]({
    contribuintes: String(contribuintes.length),
    total: String(total),
    label: contribuintes[0] ? rotuloDoVeiculo(contribuintes[0]) : 'um ônibus',
    grandeza: nome,
    genero,
    qualificador: concordarComOnibus(qualificadorDeCobertura(excluidos, nome)),
  });
}

/**
 * POR QUE NÃO HÁ NÚMERO — a frase quando NINGUÉM contribuiu.
 *
 * ---------------------------------------------------------------------------
 * A FRASE ANTERIOR MENTIA, e a mentira foi medida no cenário de carimbo futuro.
 *
 * Havia saída antecipada só para `naoSuportado === total`. Os outros quatro
 * baldes herdavam o `Record` de cobertura, que devolve
 * `'Nenhum dos 16 ônibus visíveis com estado de carga'` — e com os 16
 * carimbados à frente do relógio a tela dizia exatamente isso, enquanto 14
 * deles TINHAM estado de carga. O que eles não têm é carimbo utilizável.
 *
 * Afirmar ausência de grandeza onde a causa é o relógio manda o operador olhar
 * o sensor de bateria quando o problema é o fuso da sincronia. É o mesmo
 * defeito da dica de frequência da Subestação, que "mandava conferir o que já
 * está avariado".
 *
 * ---------------------------------------------------------------------------
 * UMA CAUSA NOMEIA, CAUSAS MISTURADAS RECUAM — a mesma disciplina de
 * `qualificadorDeCobertura`, aplicada um nível acima.
 *
 * Só é honesto nomear a causa quando ela explica TODOS os excluídos. Com oito
 * sem sinal e oito sem a coluna, dizer "os 16 estão sem sinal" é falso para
 * metade; o neutro é verdadeiro para qualquer combinação.
 *
 * `Record` sobre `keyof ExcluidosPorCausa`: um sexto balde não compila até ter
 * frase aqui. Era exatamente o furo — `naoSuportado` tinha tratamento e os
 * outros quatro caíam num default que ninguém tinha escrito de propósito.
 */
function motivoSemContribuinte(
  excluidos: ExcluidosPorCausa,
  total: number,
  campo: CampoDeVeiculo,
  veiculos: TopologiaDeFrota,
): string {
  const { nome, genero } = ROTULO_DO_CAMPO[campo];
  const causas = (Object.keys(excluidos) as Array<keyof ExcluidosPorCausa>).filter(
    (c) => excluidos[c] > 0,
  );
  const unica = causas.length === 1 ? causas[0] : null;
  if (unica !== undefined && unica !== null && excluidos[unica] === total) {
    return MOTIVO_SEM_CONTRIBUINTE[unica]({
      total: String(total),
      grandeza: nome,
      genero,
      fontes: listar(fontesSemCampo(veiculos, campo)),
    });
  }
  return `Nenhum dos ${total} ônibus visíveis contribui com ${nome}, por motivos diferentes — veja a coluna de cada um.`;
}

const MOTIVO_SEM_CONTRIBUINTE: Record<
  keyof ExcluidosPorCausa,
  (c: { total: string; grandeza: string; genero: Genero; fontes: string }) => string
> = {
  /* A única que diz "não espere" — e é por isso que vem primeiro na leitura. */
  naoSuportado: (c) =>
    `${maiuscula(c.grandeza)} não é ${CONCORDANCIA[c.genero].fornecida} por ${c.fontes} — não há o que agregar para esta frota.`,
  /* NÃO fala da grandeza: eles podem tê-la, e o que falta é poder situá-la. */
  carimboFuturo: (c) =>
    `Os ${c.total} ônibus visíveis estão com carimbo à frente do relógio do portal: a leitura deles não pode ser situada no tempo, então nenhum valor entra no agregado.`,
  semLeitura: (c) => `Nenhum dos ${c.total} ônibus visíveis reportou alguma vez.`,
  vencidos: (c) =>
    `Nenhum dos ${c.total} ônibus visíveis tem leitura corrente com ${c.grandeza}.`,
  semGrandeza: (c) =>
    `Nenhum dos ${c.total} ônibus visíveis trouxe ${c.grandeza} na última leitura.`,
};

/**
 * CONCORDÂNCIA DE GÊNERO do qualificador — pego na validação em navegador, e é
 * defeito de composição, não de tradução.
 *
 * `qualificadorDeCobertura` nasceu na Subestação, onde o substantivo contado é
 * FEMININO ("2 de 3 entradas", "as cabines"), e o recuo para o neutro devolve
 * `'consideradas'`. Na frota o substantivo é "ônibus", masculino, e a frase saía
 * na tela como:
 *
 *     "Média de 12 de 16 ônibus consideradas"
 *
 * O texto é o produto desta tela. Uma frase agramatical no KPI que o operador lê
 * primeiro corrói a confiança em TODO o resto do vocabulário — e este módulo
 * existe para o vocabulário ser confiável.
 *
 * A adaptação mora aqui, e não em `frescor.ts`, de propósito: o gênero é
 * propriedade do substantivo de CADA domínio, e o módulo compartilhado não pode
 * sabê-lo. As outras quatro frases de causa ("com leitura", "com leitura
 * corrente", "com carimbo de tempo consistente", "com <grandeza>", "de fonte que
 * fornece <grandeza>") são invariáveis em gênero e atravessam sem tocar.
 *
 * REGISTRADO PARA A PRÓXIMA FASE: o certo é `qualificadorDeCobertura` receber o
 * gênero do conjunto, em vez de cada domínio corrigir a saída. Enquanto ele não
 * receber, um sexto balde com palavra feminina chegará aqui sem tradução — há
 * teste varrendo todas as combinações de causa para pegar isso.
 */
export function concordarComOnibus(qualificador: string): string {
  return qualificador === 'consideradas' ? 'considerados' : qualificador;
}

/**
 * O construtor único dos agregados: recebe a TOPOLOGIA e aplica o critério por
 * dentro. Ver a divergência 1 do cabeçalho.
 */
function montarAgregado(
  vocabulario: FraseDeCobertura,
  veiculos: TopologiaDeFrota,
  campo: CampoDeVeiculo,
  cfg: ConfiguracaoDeFrota,
  reduzir: (valores: readonly number[]) => number,
): AgregadoDeFrota {
  const contribuintes = veiculos.filter((v) => numeroUtilizavel(v, campo, cfg) !== null);
  const valores = contribuintes
    .map((v) => numeroUtilizavel(v, campo, cfg))
    .filter((n): n is number => n !== null);
  const excluidos = discriminarPorCausaDeCampo(veiculos, campo, cfg);
  return {
    valor: valores.length > 0 ? reduzir(valores) : null,
    contribuintes: contribuintes.length,
    total: veiculos.length,
    excluidos,
    vencidosUsados: contribuintes.filter((v) => estaVencido(v, cfg)).length,
    inconsistencias: veiculos.filter(
      (v) => estadoDoCampo(v, campo, cfg) === 'inconsistencia-de-fonte',
    ).length,
    frase: fraseDoAgregado(vocabulario, contribuintes, veiculos, campo, excluidos),
  };
}

export function mediaDoCampo(
  veiculos: TopologiaDeFrota,
  campo: CampoDeVeiculo,
  cfg: ConfiguracaoDeFrota,
): AgregadoDeFrota {
  return montarAgregado(
    FRASE_DA_MEDIA,
    veiculos,
    campo,
    cfg,
    (vs) => vs.reduce((a, b) => a + b, 0) / vs.length,
  );
}

/* `somaDoCampo` foi REMOVIDO, e a remoção é decisão de domínio: NENHUM campo
   de veículo desta frota é somável. SOC é percentual, odômetro é acumulado por
   veículo, velocidade é instantânea — somar qualquer um produz um número sem
   significado físico. `energy_available_kwh` seria o único candidato e é
   impossível na fonte ativa.

   Um agregado que ninguém pode usar corretamente é um convite a usá-lo
   errado, e `FRASE_DA_SOMA` continua existindo para o dia em que houver campo
   somável — aí o construtor volta com um consumidor junto, e o vocabulário
   junto dele. */

export function minimoDoCampo(
  veiculos: TopologiaDeFrota,
  campo: CampoDeVeiculo,
  cfg: ConfiguracaoDeFrota,
): AgregadoDeFrota {
  return montarAgregado(FRASE_DO_EXTREMO, veiculos, campo, cfg, (vs) => Math.min(...vs));
}

export function maximoDoCampo(
  veiculos: TopologiaDeFrota,
  campo: CampoDeVeiculo,
  cfg: ConfiguracaoDeFrota,
): AgregadoDeFrota {
  return montarAgregado(FRASE_DO_EXTREMO, veiculos, campo, cfg, (vs) => Math.max(...vs));
}

/* ===========================================================================
 * CONTAGENS — o denominador é sempre a topologia
 * ========================================================================= */

/**
 * Contagem por estado de leitura. PARTIÇÃO: soma exatamente `total`.
 *
 * Substitui o `stats` de `COUNT(*) FROM vehicle_telemetry` com as linhas vindo
 * de `signalstore_vehicles LEFT JOIN` — DUAS RELAÇÕES CONTADAS COMO UMA. Agora
 * os KPIs derivam do MESMO array que a tabela renderiza, e duas consultas que
 * viraram uma não podem divergir.
 */
export interface ContagemDaFrota {
  total: number;
  correntes: number;
  vencidos: number;
  semLeitura: number;
  futuros: number;
  /** Modo demonstração. Inalcançável na frota; existe para a partição fechar. */
  neutros: number;
}

export function contagemDaFrota(
  veiculos: TopologiaDeFrota,
  cfg: ConfiguracaoDeFrota,
): ContagemDaFrota {
  const por = (alvo: EstadoDeLeitura) =>
    veiculos.filter((v) => estadoDeLeitura(v, cfg) === alvo).length;
  return {
    total: veiculos.length,
    correntes: por('corrente'),
    vencidos: por('vencida'),
    semLeitura: por('sem-leitura'),
    futuros: por('futura'),
    neutros: por('neutra'),
  };
}

/**
 * O KPI "SEM SINAL" — o NÚMERO e a sua DECOMPOSIÇÃO, juntos.
 *
 * ---------------------------------------------------------------------------
 * POR QUE OS DOIS SAEM DA MESMA FUNÇÃO
 *
 * Eles estavam separados, e divergiram nas duas telas — visto ao vivo no
 * navegador, com a frota de 18:
 *
 *     Mapa ao Vivo   "SEM SINAL 18"
 *                    "16 com leitura vencida · 1 nunca reportaram"
 *
 * Dezessete de dezoito. O décimo oitavo era o veículo com CARIMBO À FRENTE do
 * relógio: ele entra em `semSinal` (não tem leitura corrente) e não tinha balde
 * na frase. Uma dica que promete decompor um número e esconde um caso é a mesma
 * família de defeito que `counts.stale = pins.length - fresh.length` — o balde
 * sem nome.
 *
 * Com o número DERIVADO da mesma decomposição que a frase enumera, a divergência
 * deixa de ser possível: há invariante varrendo o espaço para provar que as
 * parcelas somam `total`.
 *
 * E a CONCORDÂNCIA DE NÚMERO é do mesmo tipo do sexto defeito de gênero: a tela
 * imprimia "1 nunca reportaram". Aqui o plural acompanha a parcela.
 */
export interface SemSinal {
  /** Quantos não têm leitura corrente. É o valor do KPI. */
  total: number;
  /**
   * O QUE O KPI IMPRIME, e `null` imprime travessão.
   *
   * Existe porque `String(total)` estava errado num caso, e o caso é o vazio:
   * ver o comentário de `frota vazia` em `semSinalDaFrota`. O número continua
   * disponível em `total` para quem precisa CONTAR; quem precisa EXIBIR usa
   * este campo, e as duas coisas deixam de poder divergir.
   */
  valor: string | null;
  /** A decomposição, enumerada. As parcelas SOMAM `total`. */
  frase: string;
  /** A frota visível é vazia? O KPI degrada como os três vizinhos. */
  vazio: boolean;
}

export function semSinalDaFrota(contagem: ContagemDaFrota): SemSinal {
  /*
    FROTA VAZIA: `total === 0` AFIRMAVA LEITURA CORRENTE SOBRE ZERO VEÍCULOS.

    Com `contagem.total === 0`, `partes` sai vazio e o ramo do fim devolvia
    "Todos os ônibus visíveis têm leitura corrente". A tela imprimia, lado a
    lado:

        ÔNIBUS VISÍVEIS  0
        SEM SINAL        0   — Todos os ônibus visíveis têm leitura corrente

    Uma afirmação universal sobre conjunto vazio é vacuamente verdadeira em
    lógica e MENTIROSA numa tela de operação: o operador lê "a frota está toda
    reportando". Os outros três KPIs da mesma linha já degradavam certo
    ("Nenhum ônibus visível") — o vocabulário existia UM CARD AO LADO.

    E é o estado que o ADR 0003 §3.2 projeta e ACEITA como "vazio honesto" de
    até 30 s entre o `migrate` e o primeiro tick. Honesto nao era.

    POR QUE O INVARIANTE NOVO NÃO PEGOU: ele prova que as parcelas somam
    `total`, e `0 = 0` passa. **Provar a soma não prova a sentença.** A guarda
    que faltava varre a FRASE sobre o espaço das contagens — inclusive o vazio.
  */
  if (contagem.total === 0) {
    return {
      total: 0,
      /* `null` e não `'0'`: "SEM SINAL 0" ao lado de "ÔNIBUS VISÍVEIS 0" se lê
         como tranquilidade. Zero por AUSÊNCIA de frota não é zero por AUSÊNCIA
         de problema, e é a mesma distinção que `mostraValor` faz na célula.

         CORREÇÃO DE UMA JUSTIFICATIVA QUE ERA FALSA: a redação anterior deste
         comentário dizia "os três KPIs vizinhos imprimem travessão". Isso valia
         só para o trio de SOC, que passa por `montarAgregado`. "COM SINAL" e
         "CARREGANDO" continuavam imprimindo zero, porque eram
         `String(contagem.correntes)` direto no JSX — o mesmo defeito, um card à
         esquerda. Agora os dois passam por `valorDeParcela`. */
      valor: null,
      frase: 'Nenhum ônibus visível para a fonte de telemetria ativa',
      vazio: true,
    };
  }
  /* As três causas de "não ter leitura corrente", e são todas as que existem:
     `ContagemDaFrota` particiona em corrente | vencida | sem-leitura | futura |
     neutra, e `neutra` é inalcançável na frota (não há simulador). Somar as três
     aqui, em vez de `total - correntes`, é o que amarra número e frase. */
  const partes = [
    contagem.vencidos > 0
      ? `${contagem.vencidos} com leitura vencida`
      : null,
    contagem.semLeitura > 0
      ? `${contagem.semLeitura} nunca ${contagem.semLeitura === 1 ? 'reportou' : 'reportaram'}`
      : null,
    contagem.futuros > 0
      ? `${contagem.futuros} com carimbo à frente do relógio`
      : null,
  ].filter((p): p is string => p !== null);
  const total = contagem.vencidos + contagem.semLeitura + contagem.futuros;
  return {
    total,
    valor: String(total),
    /* Sem nenhuma parcela o KPI é zero, e "0 sem sinal" não pede decomposição —
       enumerar baldes vazios treina o operador a ignorar a dica. Aqui a frase
       universal é LEGÍTIMA, porque o ramo do vazio saiu no topo da função e há
       pelo menos um ônibus para ela falar sobre. */
    frase: partes.length === 0 ? 'Todos os ônibus visíveis têm leitura corrente' : listar(partes),
    vazio: false,
  };
}

/** Contagem exaustiva por estado de UM campo. Soma `veiculos.length`. */
export function contagemDoCampo(
  veiculos: TopologiaDeFrota,
  campo: CampoDeVeiculo,
  cfg: ConfiguracaoDeFrota,
): Record<EstadoDoCampo, number> {
  const contagem: Record<EstadoDoCampo, number> = {
    'nao-suportado': 0,
    'inconsistencia-de-fonte': 0,
    'sem-leitura': 0,
    futura: 0,
    vencida: 0,
    'vencida-sem-grandeza': 0,
    'sem-grandeza': 0,
    corrente: 0,
  };
  for (const v of veiculos) contagem[estadoDoCampo(v, campo, cfg)] += 1;
  return contagem;
}

/* ===========================================================================
 * ESTADO DE MOVIMENTO — o badge, e o fim do `?? 0`
 * ========================================================================= */

/**
 * Velocidade a partir da qual o ônibus está EM MOVIMENTO.
 *
 * Era o literal `3` dentro de `pins.filter(...)` no Mapa. Mora aqui porque é
 * decisão operacional (ruído de GPS em veículo parado chega a 2–3 km/h), e não
 * detalhe de uma tela.
 */
export const LIMIAR_DE_MOVIMENTO_KMH = 3;

export type EstadoDeMovimento =
  | 'sem-leitura'
  | 'carimbo-futuro'
  /** Vencida: o último snapshot NÃO afirma o estado atual. */
  | 'sem-sinal'
  | 'carregando'
  | 'rodando'
  | 'parado'
  /**
   * Leitura corrente, e os campos não permitem decidir. Estado NOVO e
   * necessário: `rodando: fresh.filter(p => p.ignition && (p.speed_kmh ?? 0) > 3)`
   * transformava velocidade AUSENTE em "parado", que é afirmação e não default.
   * Sem este balde o veículo indecidível não entrava em contagem nenhuma — o
   * defeito de denominador, de novo.
   */
  | 'indeterminado';

/**
 * O badge de UM veículo.
 *
 * Ordem das decisões, cada uma com a sua razão:
 *
 *  1. Sem leitura corrente, o estado é de SINAL e não de movimento. Veículo sem
 *     sinal há 4 semanas não pode ser contado em "carregando agora" — item 1 da
 *     lista de sanity operacional, e literalmente o que
 *     `COUNT(*) FILTER (WHERE plug_charging)` fazia.
 *  2. MOVIMENTO POSITIVO vence: acima do limiar o ônibus está rodando, e
 *     rodando ele não está carregando. É a única afirmação que não depende de
 *     mais nenhum campo.
 *  3. Carga só é afirmada com `plug_charging` DECIDÍVEL e verdadeiro.
 *  4. "Parado" exige poder NEGAR a carga. Com `plug_charging` não fornecido ou
 *     ausente, dizer "Parado" numa coluna que contrasta com "Carregando" nega
 *     uma coisa que não se sabe: vira `indeterminado`.
 *  5. Ignição ligada com velocidade indecidível é `indeterminado`, não
 *     "parado".
 */
export function estadoDeMovimento(
  v: VeiculoDeFrota,
  cfg: ConfiguracaoDeFrota,
): EstadoDeMovimento {
  if (v.latest === null) return 'sem-leitura';
  if (temCarimboFuturo(v, cfg)) return 'carimbo-futuro';
  if (estaVencido(v, cfg)) return 'sem-sinal';

  const velocidade = numeroUtilizavel(v, 'speed_kmh', cfg);
  if (velocidade !== null && velocidade > LIMIAR_DE_MOVIMENTO_KMH) return 'rodando';

  const plug = valorUtilizavel(v, 'plug_charging', cfg);
  if (plug === true) return 'carregando';
  if (plug !== false) return 'indeterminado';

  const ignicao = valorUtilizavel(v, 'ignition', cfg);
  if (ignicao === false) return 'parado';
  if (ignicao === true) return velocidade !== null ? 'parado' : 'indeterminado';
  return 'indeterminado';
}

/* ===========================================================================
 * CONTRADICAO DENTRO DE UMA LEITURA CORRENTE
 * ========================================================================= */

/**
 * DOIS CAMPOS DA MESMA LEITURA SE CONTRADIZEM — e nao havia frase para isso.
 *
 * ---------------------------------------------------------------------------
 * O CASO: onibus a 31 km/h com `plug_charging: true`.
 *
 * `estadoDeMovimento` resolve para "Rodando", e a precedencia esta CERTA e
 * documentada: movimento positivo vence, porque rodando o onibus nao esta
 * carregando. Mas ao vencer, ela DESCARTA o plugue — e o plugue nao e ruido, e
 * uma afirmacao da mesma leitura, com o mesmo carimbo, dizendo o contrario.
 *
 * O `inconsistencia-de-fonte` foi construido para o caso estruturalmente
 * IDENTICO: a fonte declara nao fornecer o campo e o banco trouxe valor. Duas
 * afirmacoes incompativeis sobre o mesmo dado, e a resposta correta foi mostrar
 * a contradicao em vez de escolher um lado em silencio. Aqui a contradicao e
 * entre dois CAMPOS em vez de entre campo e declaracao da fonte, e nao havia
 * nada: a tela escolhia o lado e nao dizia que havia lado.
 *
 * A diferenca operacional e concreta. Plugue preso, sensor de velocidade com
 * ruido, ou — o pior — a sincronia misturando linhas de veiculos diferentes.
 * Nenhuma dessas tres se investiga olhando um badge que diz "Rodando".
 *
 * O QUE ESTA FUNCAO NAO FAZ: nao muda `estadoDeMovimento` e nao inventa um
 * oitavo estado. O estado continua sendo o que a precedencia decide; esta e uma
 * camada de DENUNCIA por cima, do mesmo jeito que `AvisoDeInconsistencia` nao
 * mexe em `EstadoDoCampo`.
 *
 * SO EM LEITURA CORRENTE. Com leitura vencida os dois campos sao do passado e
 * podem ter sido verdadeiros em momentos diferentes do mesmo snapshot —
 * denunciar ali seria transformar dado velho em bug.
 */
export type Contradicao = 'movimento-com-plugue' | 'parado-com-torque';

export const FRASE_DA_CONTRADICAO: Record<Contradicao, (v: VeiculoDeFrota) => string> = {
  'movimento-com-plugue': (v) =>
    `Leitura corrente contraditória: ${rotuloDoVeiculo(v)} reporta movimento e conexão de ` +
    `carga no MESMO carimbo. O estado exibido usa a velocidade, e o plugue foi descartado — ` +
    `não é dado velho, são dois campos da mesma leitura se contradizendo. Verifique o ` +
    `sensor de velocidade, o contato do plugue e se a sincronia não está misturando veículos.`,
  'parado-com-torque': (v) =>
    `Leitura corrente contraditória: ${rotuloDoVeiculo(v)} reporta torque de motor com ` +
    `velocidade zero e freio de mão acionado, no MESMO carimbo. Verifique o sensor de ` +
    `torque e o de freio.`,
};

/**
 * As contradicoes de UM veiculo. Vazio quando nao ha — e "nao ha" inclui "nao
 * da para saber": campo indecidivel nao produz contradicao, porque contradicao
 * exige DUAS afirmacoes, e ausencia nao afirma nada.
 */
export function contradicoesDoVeiculo(
  v: VeiculoDeFrota,
  cfg: ConfiguracaoDeFrota,
): readonly Contradicao[] {
  /*
    `corrente` EXPLICITO, e nao `valorUtilizavel`.

    `valorUtilizavel` aceita `ESTADOS_COM_VALOR = ['corrente', 'vencida']`, e a
    inclusao do vencido la esta CERTA pelo motivo escrito naquele comentario (os
    agregados declaram quantos vencidos usaram em vez de esvaziar o KPI). Aqui
    ela seria errada: com leitura vencida os dois campos sao do passado e podem
    ter sido verdadeiros em momentos diferentes do mesmo snapshot. Denunciar
    contradicao ali transformaria dado velho em bug de dado.

    Pego pelo proprio teste desta funcao, que e o que a distincao merece.

    `corrente` tambem e o que exclui `inconsistencia-de-fonte`: uma fonte que
    declara nao fornecer `plug_charging` e trouxe valor JA tem banner proprio, e
    dois banners para o mesmo defeito na mesma linha e ruido.
  */
  const leituraDe = (campo: CampoDeVeiculo): number | boolean | string | null =>
    estadoDoCampo(v, campo, cfg) === 'corrente' ? LEITURA_DO_CAMPO[campo](v) : null;
  const numeroDe_ = (campo: CampoDeVeiculo): number | null => {
    const bruto = leituraDe(campo);
    return typeof bruto === 'number' && Number.isFinite(bruto) ? bruto : null;
  };
  const achadas: Contradicao[] = [];
  const velocidade = numeroDe_('speed_kmh');
  const plugue = leituraDe('plug_charging');
  if (velocidade !== null && velocidade > LIMIAR_DE_MOVIMENTO_KMH && plugue === true) {
    achadas.push('movimento-com-plugue');
  }
  const torque = numeroDe_('motor_torque_nm');
  const freio = leituraDe('handbrake');
  if (velocidade === 0 && torque !== null && torque > 0 && freio === true) {
    achadas.push('parado-com-torque');
  }
  return achadas;
}

/** As contradicoes da frota, com o veiculo de cada uma. */
export function contradicoesDeLeitura(
  veiculos: TopologiaDeFrota,
  cfg: ConfiguracaoDeFrota,
): ReadonlyArray<{ veiculo: VeiculoDeFrota; contradicao: Contradicao }> {
  const fora: Array<{ veiculo: VeiculoDeFrota; contradicao: Contradicao }> = [];
  for (const veiculo of veiculos) {
    for (const contradicao of contradicoesDoVeiculo(veiculo, cfg)) {
      fora.push({ veiculo, contradicao });
    }
  }
  return fora;
}

/**
 * O banner, pronto, com concordancia de NUMERO.
 *
 * `null` quando nao ha contradicao: um banner permanente em zero treina o
 * operador a ignorar banners, que e o argumento do card de carimbo futuro.
 */
export function fraseDeContradicaoDeLeitura(
  achadas: ReadonlyArray<{ veiculo: VeiculoDeFrota; contradicao: Contradicao }>,
): string | null {
  if (achadas.length === 0) return null;
  const f = formasPara(achadas.length);
  const detalhes = achadas.map((a) => FRASE_DA_CONTRADICAO[a.contradicao](a.veiculo));
  const cabeca =
    achadas.length === 1
      ? `1 leitura corrente ${f.ter} campos que se contradizem`
      : `${achadas.length} leituras correntes ${f.ter} campos que se contradizem`;
  return `${cabeca}. ${detalhes.join(' ')}`;
}

export const ROTULO_DO_MOVIMENTO: Record<EstadoDeMovimento, string> = {
  'sem-leitura': 'Nunca reportou',
  'carimbo-futuro': 'Carimbo à frente',
  'sem-sinal': 'Sem sinal',
  carregando: 'Carregando',
  rodando: 'Rodando',
  parado: 'Parado',
  indeterminado: 'Indeterminado',
};

/** A explicação do badge. `indeterminado` e `sem-sinal` sem frase seriam
 *  rótulos que o operador não sabe o que fazer com. */
export const FRASE_DO_MOVIMENTO: Record<EstadoDeMovimento, (fonte: string) => string> = {
  'sem-leitura': () => 'Cadastrado e sem nenhuma leitura de telemetria.',
  'carimbo-futuro': () =>
    'O carimbo da leitura está à frente do relógio do portal: não é possível afirmar o estado atual.',
  'sem-sinal': () =>
    'A última mensagem está fora do prazo de frescor — o que aparece é o último snapshot, não o estado de agora.',
  carregando: () => 'Plugue conectado na leitura corrente.',
  rodando: () => `Acima de ${LIMIAR_DE_MOVIMENTO_KMH} km/h na leitura corrente.`,
  parado: () => 'Leitura corrente, sem movimento e sem plugue conectado.',
  indeterminado: (fonte) =>
    `A leitura é corrente, mas não traz os campos que decidem o estado — ${fonte} pode não fornecer velocidade, ignição ou conexão de carga.`,
};

/** Contagem por estado de movimento. PARTIÇÃO: soma `veiculos.length`. */
export function contagemDeMovimento(
  veiculos: TopologiaDeFrota,
  cfg: ConfiguracaoDeFrota,
): Record<EstadoDeMovimento, number> {
  const contagem: Record<EstadoDeMovimento, number> = {
    'sem-leitura': 0,
    'carimbo-futuro': 0,
    'sem-sinal': 0,
    carregando: 0,
    rodando: 0,
    parado: 0,
    indeterminado: 0,
  };
  for (const v of veiculos) contagem[estadoDeMovimento(v, cfg)] += 1;
  return contagem;
}

/* ===========================================================================
 * POSIÇÃO E O RESUMO DO MAPA (§6.2)
 * ========================================================================= */

/**
 * A posição UTILIZÁVEL do veículo, ou `null`.
 *
 * Quatro recusas, cada uma por um motivo:
 *  - `gps_lat`/`gps_lon` não fornecidos pela fonte, ou ausentes na leitura;
 *  - carimbo à frente do relógio — a posição existe e não tem quando (vem de
 *    `numeroUtilizavel`, que exclui `futura`);
 *  - valor fora do domínio;
 *  - exatamente `(0, 0)` — o Golfo da Guiné é o que um GPS sem fix reporta, e
 *    desenhar um ônibus de São Paulo lá é pior que não desenhar. O servidor já
 *    filtra isso no rastro; a recusa mora aqui para valer nos dois lugares.
 *
 * Aceita posição VENCIDA de propósito: última posição conhecida é informação, e
 * o pin cinza a apresenta como tal. O que não pode é ela virar "onde o ônibus
 * está" — CLAUDE.md, nessas palavras.
 */
export function posicaoDoVeiculo(
  v: VeiculoDeFrota,
  cfg: ConfiguracaoDeFrota,
): [number, number] | null {
  const lat = numeroUtilizavel(v, 'gps_lat', cfg);
  const lon = numeroUtilizavel(v, 'gps_lon', cfg);
  if (lat === null || lon === null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  if (lat === 0 && lon === 0) return null;
  return [lat, lon];
}

export interface ResumoDoMapa {
  /** A TOPOLOGIA visível. É sobre este número que o cabeçalho declara. */
  total: number;
  /** Leitura corrente E posição utilizável. */
  reportando: number;
  /** Sem leitura corrente — inclui quem nunca reportou, o vencido e o futuro. */
  semSinal: number;
  /** Leitura corrente e SEM posição utilizável. DISTINTO de `semSinal`. */
  semPosicao: number;
  /** Pins desenháveis: qualquer posição utilizável, corrente ou última. */
  comPosicao: number;
  porMovimento: Record<EstadoDeMovimento, number>;
  /** O cabeçalho, pronto. */
  frase: string;
}

/**
 * O resumo do Mapa ao Vivo — o pior denominador da entrega, antes desta função.
 *
 * O que havia: a SQL cortava `updated_at > NOW() - 10min` e
 * `gps_lat IS NOT NULL`. Com 10 de 16 parados, `pins` tinha ~6 e o cabeçalho
 * dizia "6 veículos com GPS ativo". `counts.stale = pins.length - fresh.length`
 * só enxergava a faixa entre 2 e 10 minutos: OS 10 DESAPARECIDOS NÃO ENTRAVAM
 * EM CONTAGEM NENHUMA. O denominador era quem respondeu.
 *
 * Pior: `isStale` comparava contra `Date.now()`, o relógio do NAVEGADOR — um
 * operador com a máquina atrasada via a frota inteira fresca —, e devolvia
 * `false` para `NaN`, tratando carimbo ilegível como fresco. Aqui a idade vem
 * do servidor, assinada, e o estado sai do classificador único.
 *
 * As três contagens do cabeçalho PARTICIONAM a topologia — há invariante de
 * teste varrendo o espaço para garantir
 * `reportando + semSinal + semPosicao === total`.
 *
 * "Sem sinal" e "sem posição" são DISTINTOS, e a distinção é a informação: o
 * primeiro é problema de IDADE, o segundo de GPS. Veículo vencido E sem GPS
 * conta em `semSinal`, porque com a leitura fora de prazo a ausência de posição
 * também não pode ser afirmada sobre o agora — a causa dominante é a idade.
 */
export function resumoDoMapa(
  veiculos: TopologiaDeFrota,
  cfg: ConfiguracaoDeFrota,
): ResumoDoMapa {
  const correntes = veiculos.filter((v) => temLeituraCorrente(v, cfg));
  const reportando = correntes.filter((v) => posicaoDoVeiculo(v, cfg) !== null).length;
  const semPosicao = correntes.length - reportando;
  const semSinal = veiculos.length - correntes.length;
  const partes = [
    `${reportando} de ${veiculos.length} reportando`,
    semSinal > 0 ? `${semSinal} sem sinal` : null,
    semPosicao > 0 ? `${semPosicao} sem posição` : null,
  ].filter((p): p is string => p !== null);
  return {
    total: veiculos.length,
    reportando,
    semSinal,
    semPosicao,
    comPosicao: veiculos.filter((v) => posicaoDoVeiculo(v, cfg) !== null).length,
    porMovimento: contagemDeMovimento(veiculos, cfg),
    frase:
      veiculos.length === 0
        ? 'Nenhum ônibus visível para a fonte de telemetria ativa'
        : partes.join(' · '),
  };
}

/* ===========================================================================
 * MANOBRISTA (§6.5) — candidatura à fila NÃO é frescor
 * ========================================================================= */

/**
 * A posição está dentro da janela de CANDIDATURA à fila do pátio?
 *
 * `MANOBRISTA_POSITION_WINDOW_SECONDS` (900 s) é a única exceção nomeada ao
 * limiar único, e a consequência é OBRIGATÓRIA: um veículo de 8 min é candidato
 * à fila E está stale, e a linha tem de dizer as duas coisas. Esta função
 * responde só a primeira metade; `estadoDeLeitura` responde a segunda.
 *
 * Idade negativa NÃO é candidata: carimbo à frente do relógio não situa a
 * posição no tempo, e "está no pátio" sobre isso é afirmação sem base.
 */
export function dentroDaJanelaDeFila(
  v: VeiculoDeFrota,
  janelaSegundos: number,
): boolean {
  return v.latest !== null && v.latest.ageSeconds >= 0 && v.latest.ageSeconds <= janelaSegundos;
}

/**
 * A ressalva de posição de uma linha da fila. `null` quando a leitura é
 * corrente e não há ressalva a fazer.
 *
 * `inside = dist_km <= radiusKm` sobre GPS de 14 minutos era apresentado como
 * "está no pátio". POSIÇÃO CONHECIDA NÃO É POSIÇÃO ATUAL.
 */
export function ressalvaDePosicao(
  v: VeiculoDeFrota,
  cfg: ConfiguracaoDeFrota,
  formatarIdade: (segundos: number) => string,
): string | null {
  if (v.latest === null) return 'Sem posição: este ônibus nunca reportou.';
  if (temCarimboFuturo(v, cfg)) {
    return `Posição com carimbo ${formatarIdade(v.latest.ageSeconds)}, à frente do relógio: não situável no tempo.`;
  }
  if (estaVencido(v, cfg)) {
    return `Última posição conhecida há ${formatarIdade(v.latest.ageSeconds)} — não é a posição atual.`;
  }
  return null;
}

/* ===========================================================================
 * CENTRAL DE OPERAÇÕES (§6.4) — a célula diz QUAL das causas
 * ========================================================================= */

/**
 * Por que não há previsão de término de carga.
 *
 * `tempoRestanteMin` exige potência `> 5` e o Portal VGPL não fornece
 * `charging_power_kw`: a coluna é SEMPRE `null`, e antes imprimia `"—"` sem
 * dizer nada. Duas das cinco colunas do quadrante morrem estruturalmente, e o
 * operador precisa saber que não é uma falha a reportar.
 *
 * Os estados de `EstadoDoCampo` cobrem a potência; os dois acréscimos cobrem o
 * resto da conta. São deduzidos dos campos que o contrato já publica — não
 * exigem coluna nova:
 *
 *  - `'sem-soc'` — há potência corrente e não há SOC: sem estado de carga não se
 *    calcula quanto falta.
 *  - `'sem-capacidade'` — há potência E há SOC, e ainda assim não há previsão.
 *    Por eliminação, o que falta é a capacidade da bateria no cadastro do
 *    veículo, que é a terceira parcela da conta. É dedução, e está registrada
 *    como tal: se o contrato passar a publicar um `capacidadeCadastrada`, este
 *    ramo deve passar a lê-lo em vez de deduzir.
 */
export type CausaDoTempoRestante = EstadoDoCampo | 'ok' | 'sem-soc' | 'sem-capacidade';

export function causaDoTempoRestante(
  entrada: {
    leitura: ComLeitura<LeituraDatada>;
    unsupportedFields: readonly UnsupportedField[];
    potenciaKw: number | null;
    socPct: number | null;
    tempoRestanteMin: number | null;
  },
  cfg: ConfiguracaoDeFrota,
): CausaDoTempoRestante {
  if (entrada.tempoRestanteMin !== null) return 'ok';
  const daPotencia = estadoDeValor(
    entrada.leitura,
    !fonteNaoFornece(entrada.unsupportedFields, 'charging_power_kw'),
    entrada.potenciaKw,
    cfg,
  );
  /* A potência explica a ausência em todos os estados menos `corrente`. */
  if (daPotencia !== 'corrente') return daPotencia;
  if (entrada.socPct === null) return 'sem-soc';
  return 'sem-capacidade';
}

/**
 * A frase da causa. `Record` exaustivo sobre a união inteira — inclusive os oito
 * estados de campo, porque cada um manda o operador para um lugar diferente.
 */
export const FRASE_DO_TEMPO_RESTANTE: Record<
  CausaDoTempoRestante,
  (fonte: string) => string
> = {
  ok: () => 'Previsão calculada a partir do estado de carga e da potência medida.',
  'nao-suportado': (fonte) =>
    `Sem previsão: ${fonte} não fornece potência de carga, que é uma das três parcelas da conta. Não há previsão a calcular para esta frota.`,
  'inconsistencia-de-fonte': (fonte) =>
    `Sem previsão: ${fonte} declara não fornecer potência de carga e a leitura trouxe valor. O número não é confiável e não entra na conta.`,
  'sem-leitura': () => 'Sem previsão: este ônibus nunca reportou telemetria.',
  futura: () =>
    'Sem previsão: o carimbo da leitura está à frente do relógio do portal, e nada pode ser projetado a partir dela.',
  vencida: () =>
    'Sem previsão: a leitura está fora do prazo de frescor, e projetar tempo de carga a partir do último snapshot afirmaria uma sessão que pode ter terminado.',
  'vencida-sem-grandeza': () =>
    'Sem previsão: a leitura está fora do prazo e não trouxe potência de carga.',
  'sem-grandeza': () => 'Sem previsão: a leitura é corrente e não trouxe potência de carga.',
  corrente: () => 'Previsão calculada a partir do estado de carga e da potência medida.',
  'sem-soc': () =>
    'Sem previsão: há potência de carga e não há estado de carga, e sem SOC não se calcula quanto falta.',
  'sem-capacidade': () =>
    'Sem previsão: há potência e há estado de carga, mas a capacidade da bateria não está cadastrada para este ônibus.',
};

/**
 * A ressalva de TRUNCAMENTO do quadrante de frota.
 *
 * `telemetria` tem `LIMIT 200` e os KPIs falam do total: com o limite atingido a
 * tabela mostra 200 linhas e o KPI diz "N veículos", sem nada que permita à tela
 * declarar que está vendo um recorte. `null` quando não há truncamento — uma
 * ressalva permanente sobre um recorte que não existe treina o operador a
 * ignorá-la.
 */
export function ressalvaDeTruncamento(
  amostrados: number,
  totalVisivel: number,
): string | null {
  /*
    CAMPO AUSENTE NAO VIRA "undefined" NA TELA — achado pela varredura de DOM.

    Com um envelope sem `amostrados`/`totalVisivel` (servidor antigo, rota
    parcial, resposta truncada), a comparacao `undefined >= undefined` e `false`
    e a frase saia "Mostrando undefined de undefined onibus visiveis". Nao e
    hipotese: foi o que o render do Mapa imprimiu.

    E a mesma familia que esta fase persegue — ausencia apresentada como valor —
    na frase que existe para DECLARAR que o valor e parcial. `null` e a resposta
    honesta: sem os dois numeros nao da para afirmar recorte nenhum.
  */
  if (!Number.isFinite(amostrados) || !Number.isFinite(totalVisivel)) return null;
  if (amostrados >= totalVisivel) return null;
  return `Mostrando ${amostrados} de ${totalVisivel} ônibus visíveis — a lista está truncada e os indicadores acima falam do total.`;
}

/* ===========================================================================
 * RECURSOS SEM FONTE (§6.3 e §6.6) — estado vazio HONESTO
 * ========================================================================= */

/**
 * A frase de um RECURSO que a fonte ativa não sustenta.
 *
 * SEM "ainda". O advérbio é o defeito: "A fonte de desempenho ainda não está
 * integrada" e "Sem histórico suficiente" prometem que esperar resolve, e não
 * resolve — `vehicle_performance_samples` não tem escritor vivo, e
 * `battery_health_estimated` deriva de `signalstore_raw`, que o Portal VGPL não
 * escreve. A MV nunca terá uma linha `82xxx`.
 *
 * A frase nomeia TRÊS coisas, porque é o mínimo para o operador entender que
 * não é falha a reportar: a fonte ativa, a grandeza que falta, e a
 * consequência.
 */
export function fraseDeRecursoNaoSuportado(
  recurso: RecursoDeFrota,
  sourceLabel: string,
  grandezaDeOrigem: string,
): string {
  return FRASE_DE_RECURSO[recurso](sourceLabel, grandezaDeOrigem);
}

/**
 * Uma frase POR RECURSO, e não um template com o nome interpolado.
 *
 * A versão anterior montava `Não há ${ROTULO_DO_RECURSO[recurso]} a exibir` com
 * o rótulo já carregando artigo, e saía na tela como **"Não há a estimativa de
 * saúde da bateria a exibir"**. Tirar o artigo do rótulo não resolve: os três
 * recursos têm gêneros diferentes ("a estimativa", "o rastro", "a série"), e a
 * concordância cairia no particípio também ("derivada"/"derivado").
 *
 * Frase por recurso é mais verbosa e é a única forma que produz português
 * correto nos três casos — e o texto É o produto destas telas.
 *
 * `Record` exaustivo: um quarto recurso não compila até ganhar a sua frase, em
 * vez de herdar um template que não serve para ele.
 */
const FRASE_DE_RECURSO: Record<RecursoDeFrota, (fonte: string, origem: string) => string> = {
  soh_pct: (fonte, origem) =>
    `A fonte de telemetria ativa (${fonte}) não fornece ${origem}, que é a grandeza de onde o SOH é estimado. Não há curva a exibir para esta frota.`,
  gps_trail: (fonte, origem) =>
    `A fonte de telemetria ativa (${fonte}) não fornece ${origem}, de onde o rastro é reconstruído. Não há rastro a exibir para este ônibus — e isto não é prova de que ele ficou parado.`,
  performance_samples: (fonte, origem) =>
    `A fonte de telemetria ativa (${fonte}) não fornece ${origem}, de onde a distância percorrida é derivada. Não há série de desempenho a exibir para esta frota.`,
};

/**
 * Cobertura declarada com o denominador que ela realmente tem.
 *
 * `coveragePct = eligible / rows` tem como denominador QUEM TEM TURNO, não a
 * frota. Chamar isso de "Cobertura" ao lado de um KPI de frota é a tela
 * atestando um recorte que o número não tem.
 */
export function fraseDeCobertura(
  coveragePct: number | null,
  comTurno: number,
  totalVisivel: number,
): string {
  if (coveragePct === null) return 'Cobertura não apurável no período';
  if (totalVisivel === 0) return 'Nenhum ônibus visível no período';
  return comTurno >= totalVisivel
    ? `Sobre os ${totalVisivel} ônibus visíveis`
    : `Sobre ${comTurno} de ${totalVisivel} ônibus visíveis — o denominador é quem tem turno atribuído, não a frota`;
}
